/**
 * FEATURE: BATTLE_MODE
 * 
 * WordHitBattle - Battle Mode versie van WordHit
 * Volledige game logica gekopieerd van WordHit.jsx
 * 
 * IMPORTANT: Battle Mode is COMPLETELY SEPARATE from daily WordHit
 * - NO User class modifications (score, numbersPlayed, streak, lastWonId, finishedIdNumbers, etc.)
 * - NO competition data updates
 * - NO daily game tracking
 * - All battle data is stored ONLY in UserBattle class
 * - hasWon is ONLY a local state variable, does NOT modify User class
 * 
 * Om terug te draaien:
 * - Verwijder dit bestand
 * - Verwijder import en gebruik in WordHit.jsx
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import Parse from "../parseClient";
import "./WordHit.css";
import "./HeaderSquares.css";
import { isBattleModeEnabled } from "../utils/featureFlags";
import { startBattle as startBattleWithOpponent } from "../utils/battleMode";
import { getBattleGame as getBattleGameFromCache, getBattleGameByIdNumber as getBattleGameByIdNumberFromCache } from "../utils/battleGamesCache";
import { Check, ListX, WifiOff } from "lucide-react";
import { requestPushNotificationPermission, hasAskedForPushPermission } from "../utils/notificationService";
import ConfettiOverlay from "./ConfettiOverlay";
import BattleChatModal from "./BattleChatModal";
import { ensureDictionaryLoaded, isDictionaryReady, isValidWord, MAX_GUESS_WORD_LENGTH } from "../utils/wordDictionary";
import { isDictionaryGuessEnabled } from "../utils/featureFlags";
import { rewardForGuess } from "../utils/scores";

// Constants (same as WordHit)
const GRID_SIZE = 7;
/** Duur (ms) van één volledige grid-flip (gesynchroniseerd met cell flip animatie) */
const GRID_FLIP_DURATION_MS = 1100;
/** Battle ends when first player reaches this many words total (across all games) */
const BATTLE_WIN_WORD_COUNT = 10;
/** Chat modal: poll for new messages every N ms while the modal is open */
const CHAT_POLL_INTERVAL_MS = 4000;

// Local storage helpers for per-battle-game wrong guessed words
function getStoredBattleWrongWords(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("battleWrongGuessedWords");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const value = parsed[String(key)] ?? null;
      console.log("[WordHitBattle][WrongWords] load", {
        key: String(key),
        found: Array.isArray(value),
        length: Array.isArray(value) ? value.length : null,
      });
      return value;
    }
  } catch (error) {
    console.warn("[WordHitBattle] Failed to read battle wrong guessed words", error);
  }
  return null;
}

function storeBattleWrongWords(key, words) {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem("battleWrongGuessedWords");
    let parsed = {};
    if (raw) {
      try {
        parsed = JSON.parse(raw) || {};
      } catch (parseError) {
        console.warn("[WordHitBattle] Failed to parse battle wrong guessed words", parseError);
        parsed = {};
      }
    }
    parsed[String(key)] = Array.isArray(words) ? words : [];
    localStorage.setItem("battleWrongGuessedWords", JSON.stringify(parsed));
    console.log("[WordHitBattle][WrongWords] save", {
      key: String(key),
      length: Array.isArray(words) ? words.length : null,
      preview: Array.isArray(words) ? words.slice(0, 10) : null,
    });
  } catch (error) {
    console.warn("[WordHitBattle] Failed to persist battle wrong guessed words", error);
  }
}

// Check if Battle Mode is enabled
if (!isBattleModeEnabled()) {
  console.warn("[WordHitBattle] Battle Mode is disabled");
}

// ========== HELPER FUNCTIONS (from WordHit.jsx) ==========

/*
  mulberry32(seed)
  -> deterministic RNG. Same seed -> same sequence.
*/
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967295;
  };
}

/*
  createEmptyBoard()
  -> returns GRID_SIZE x GRID_SIZE array filled with ""
*/
function createEmptyBoard() {
  return Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, () => "")
  );
}

const DIRECTION_DELTAS = {
  H: { dr: 0, dc: 1 },
  V: { dr: 1, dc: 0 },
  DR: { dr: 1, dc: 1 },
  DL: { dr: 1, dc: -1 },
};

const ALL_DIRECTIONS = ["H", "V", "DR", "DL"];

function getDirectionDelta(dir) {
  return DIRECTION_DELTAS[dir] || null;
}

function cloneBoard(board) {
  return board.map((row) => row.slice());
}

function getValidStartsForDirection(board, letters, dir) {
  const size = board.length;
  const dirDelta = getDirectionDelta(dir);
  if (!dirDelta) return [];
  const starts = [];

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const positions = [];
      let fits = true;
      for (let i = 0; i < letters.length; i++) {
        const rr = r + dirDelta.dr * i;
        const cc = c + dirDelta.dc * i;
        if (rr < 0 || cc < 0 || rr >= size || cc >= size) { fits = false; break; }
        const cell = board[rr][cc];
        if (cell && cell !== letters[i]) { fits = false; break; }
        positions.push({ rr, cc, overlap: !!cell });
      }
      if (!fits) continue;

      const positionsSet = new Set(positions.map((p) => `${p.rr},${p.cc}`));

      for (const pos of positions) {
        if (pos.overlap) continue;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue;
            const nr = pos.rr + dr;
            const nc = pos.cc + dc;
            if (nr < 0 || nc < 0 || nr >= size || nc >= size) continue;
            if (positionsSet.has(`${nr},${nc}`)) continue;
            if (board[nr][nc]) { fits = false; break; }
          }
          if (!fits) break;
        }
        if (!fits) break;
      }
      if (!fits) continue;

      const startPos = positions[0];
      const endPos = positions[positions.length - 1];
      const hasOverlap = positions.some((p) => p.overlap);

      // Check that words in the same line don't touch or cross each other
      if (hasOverlap) {
        const overlappingPositions = positions.filter((p) => p.overlap);
        if (overlappingPositions.length > 1) {
          overlappingPositions.sort((a, b) => {
            const aIndex = positions.findIndex((p) => p.rr === a.rr && p.cc === a.cc);
            const bIndex = positions.findIndex((p) => p.rr === b.rr && p.cc === b.cc);
            return aIndex - bIndex;
          });
          let isContiguous = true;
          for (let i = 1; i < overlappingPositions.length; i++) {
            const prev = overlappingPositions[i - 1];
            const curr = overlappingPositions[i];
            const expectedR = prev.rr + dirDelta.dr;
            const expectedC = prev.cc + dirDelta.dc;
            if (curr.rr !== expectedR || curr.cc !== expectedC) {
              isContiguous = false;
              break;
            }
          }
          if (isContiguous && overlappingPositions.length >= 2) {
            fits = false;
          }
        } else if (overlappingPositions.length === 1) {
          const overlapPos = overlappingPositions[0];
          const beforeR = overlapPos.rr - dirDelta.dr;
          const beforeC = overlapPos.cc - dirDelta.dc;
          const afterR = overlapPos.rr + dirDelta.dr;
          const afterC = overlapPos.cc + dirDelta.dc;
          
          const hasBefore = beforeR >= 0 && beforeC >= 0 && beforeR < size && beforeC < size &&
            !positionsSet.has(`${beforeR},${beforeC}`) && board[beforeR][beforeC];
          const hasAfter = afterR >= 0 && afterC >= 0 && afterR < size && afterC < size &&
            !positionsSet.has(`${afterR},${afterC}`) && board[afterR][afterC];
          
          if (hasBefore || hasAfter) {
            fits = false;
          }
        }
      }
      if (!fits) continue;

      if (!startPos.overlap) {
        const prevR = startPos.rr - dirDelta.dr;
        const prevC = startPos.cc - dirDelta.dc;
        if (
          prevR >= 0 && prevC >= 0 && prevR < size && prevC < size &&
          !positionsSet.has(`${prevR},${prevC}`) &&
          board[prevR][prevC]
        ) {
          fits = false;
        }
      }
      if (!fits) continue;

      if (!endPos.overlap) {
        const nextR = endPos.rr + dirDelta.dr;
        const nextC = endPos.cc + dirDelta.dc;
        if (
          nextR >= 0 && nextC >= 0 && nextR < size && nextC < size &&
          !positionsSet.has(`${nextR},${nextC}`) &&
          board[nextR][nextC]
        ) {
          fits = false;
        }
      }
      if (!fits) continue;

      starts.push({ r, c, dir, hasOverlap });
    }
  }

  return starts;
}

function placeLettersOnBoard(board, letters, placement) {
  const dirDelta = getDirectionDelta(placement.dir);
  for (let i = 0; i < letters.length; i++) {
    const rr = placement.r + dirDelta.dr * i;
    const cc = placement.c + dirDelta.dc * i;
    board[rr][cc] = letters[i];
  }
}

function shuffleWithRng(arr, rng) {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  return copy;
}

function buildDirectionOrder(priorityGroups, rng) {
  return priorityGroups.reduce((order, group) => {
    const normalized = Array.isArray(group) ? group : [group];
    return order.concat(shuffleWithRng(normalized, rng));
  }, []);
}

function attemptPlacementRecursive(
  words,
  lettersList,
  index,
  board,
  placements,
  rng,
  dirPlans,
  verticalPlaced,
  crossPlaced,
  diagonalPlaced,
  requireVertical,
  requireCross,
  requireDiagonal
) {
  if (index >= words.length) {
    if (requireVertical && !verticalPlaced) {
      return null;
    }
    if (requireCross && !crossPlaced) {
      return null;
    }
    if (requireDiagonal && !diagonalPlaced) {
      return null;
    }
    return { board, placements, verticalPlaced, crossPlaced, diagonalPlaced };
  }

  const planIndex = Math.min(index, dirPlans.length - 1);
  const priorityGroups = dirPlans[planIndex] || [ALL_DIRECTIONS];
  const directionOrder = buildDirectionOrder(priorityGroups, rng);
  const letters = lettersList[index];
  const word = words[index];

  for (const dir of directionOrder) {
    const starts = getValidStartsForDirection(board, letters, dir);
    if (starts.length === 0) continue;

    const overlapStarts = [];
    const nonOverlapStarts = [];
    for (const s of starts) {
      if (s.hasOverlap) {
        overlapStarts.push(s);
      } else {
        nonOverlapStarts.push(s);
      }
    }

    const startOrder = [
      ...shuffleWithRng(overlapStarts, rng),
      ...shuffleWithRng(nonOverlapStarts, rng),
    ];
    for (const start of startOrder) {
      const nextBoard = cloneBoard(board);
      placeLettersOnBoard(nextBoard, letters, start);
      const { hasOverlap, ...placementWithoutOverlap } = start;
      const result = attemptPlacementRecursive(
        words,
        lettersList,
        index + 1,
        nextBoard,
        placements.concat({ word, ...placementWithoutOverlap }),
        rng,
        dirPlans,
        verticalPlaced || dir === "V",
        crossPlaced || hasOverlap,
        diagonalPlaced || dir === "DR" || dir === "DL",
        requireVertical,
        requireCross,
        requireDiagonal
      );
      if (result) {
        return result;
      }
    }
  }

  return null;
}

function tryPlaceWordsWithPlans(words, rng, dirPlans, options = {}) {
  const {
    requireVertical = true,
    requireCross = false,
    requireDiagonal = false,
  } = options;
  const board = createEmptyBoard();
  const lettersList = words.map((w) => w.split(""));
  const result = attemptPlacementRecursive(
    words,
    lettersList,
    0,
    board,
    [],
    rng,
    dirPlans,
    false,
    false,
    false,
    requireVertical,
    requireCross,
    requireDiagonal
  );
  if (!result) return null;
  return {
    board: result.board,
    placements: result.placements,
    verticalPlaced: result.verticalPlaced,
    crossPlaced: result.crossPlaced,
    diagonalPlaced: result.diagonalPlaced,
  };
}

function expandPlans(basePlans, count) {
  if (!basePlans || basePlans.length === 0) {
    return Array.from({ length: count }, () => [ALL_DIRECTIONS]);
  }
  const expanded = [];
  for (let i = 0; i < count; i++) {
    expanded.push(basePlans[Math.min(i, basePlans.length - 1)]);
  }
  return expanded;
}

function revealAllOfLetter(letter, revealed, board) {
  const newRev = revealed.map((row) => row.slice());
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (board[r][c] === letter) {
        newRev[r][c] = true;
      }
    }
  }
  return newRev;
}

function revealWord(word, placements, revealed) {
  const placement = placements.find((p) => p.word === word);
  if (!placement) return revealed;
  const newRev = revealed.map((row) => row.slice());
  for (let i = 0; i < placement.word.length; i++) {
    let rr = placement.r;
    let cc = placement.c;
    if (placement.dir === "H") {
      cc = placement.c + i;
    } else if (placement.dir === "V") {
      rr = placement.r + i;
    } else if (placement.dir === "DR") {
      rr = placement.r + i; cc = placement.c + i;
    } else if (placement.dir === "DL") {
      rr = placement.r + i; cc = placement.c - i;
    }
    newRev[rr][cc] = true;
  }
  return newRev;
}

/** Apply actions from index fromIdx to toIdx (inclusive) to initial rev/guessed; returns { rev, guessed }. */
function applyActionsRangeToRevealed(initialRev, initialGuessed, actions, fromIdx, toIdx, board, placements) {
  if (!placements || !board || !Array.isArray(actions) || fromIdx > toIdx) {
    return { rev: initialRev.map((row) => row.slice()), guessed: [...initialGuessed] };
  }
  let rev = initialRev.map((row) => row.slice());
  let guessed = [...initialGuessed];
  const getPayload = (action) => (action.action && typeof action.action === "object" ? action.action : action);
  for (let i = fromIdx; i <= toIdx; i++) {
    const payload = getPayload(actions[i]);
    if (!payload || !payload.type) continue;
    if (payload.type === "click") {
      const { r, c } = payload;
      const rn = typeof r === "number" ? r : parseInt(r, 10);
      const cn = typeof c === "number" ? c : parseInt(c, 10);
      if (rn >= 0 && rn < GRID_SIZE && cn >= 0 && cn < GRID_SIZE) {
        const letter = board[rn][cn];
        if (letter && letter !== "") {
          rev = revealAllOfLetter(letter, rev, board);
        } else {
          rev[rn] = rev[rn].slice();
          rev[rn][cn] = true;
        }
      }
    } else if (payload.type === "guess" && payload.revealsLetters) {
      const { word } = payload;
      if (word) {
        for (let j = 0; j < word.length; j++) {
          rev = revealAllOfLetter(word[j], rev, board);
        }
      }
    } else if (payload.type === "guess" && payload.correct) {
      const { word } = payload;
      if (word && !guessed.includes(word)) {
        guessed.push(word);
        rev = revealWord(word, placements, rev);
        for (let j = 0; j < word.length; j++) {
          rev = revealAllOfLetter(word[j], rev, board);
        }
      }
    } else if (payload.type === "word_solved" && payload.word) {
      const { word } = payload;
      if (word) {
        rev = revealWord(word, placements, rev);
        for (let j = 0; j < word.length; j++) {
          rev = revealAllOfLetter(word[j], rev, board);
        }
      }
    }
  }
  return { rev, guessed };
}

/** Returns list of { r, c } for all cells in the word's placement. */
function getWordCells(word, placements) {
  const placement = placements.find((p) => p.word === word);
  if (!placement) return [];
  const cells = [];
  for (let i = 0; i < placement.word.length; i++) {
    let rr = placement.r;
    let cc = placement.c;
    if (placement.dir === "H") cc = placement.c + i;
    else if (placement.dir === "V") rr = placement.r + i;
    else if (placement.dir === "DR") { rr = placement.r + i; cc = placement.c + i; }
    else if (placement.dir === "DL") { rr = placement.r + i; cc = placement.c - i; }
    cells.push({ r: rr, c: cc });
  }
  return cells;
}

/** Build expanded replay steps: clicks = 1 step, guess/word_solved = 1 step per new letter (word order). */
function buildExpandedReplaySteps(actions, start, end, initialRev, initialGuessed, board, placements) {
  const getPayload = (a) => (a.action && typeof a.action === "object" ? a.action : a);
  const steps = [];
  let rev = initialRev.map((row) => row.slice());
  let guessed = [...initialGuessed];

  for (let i = start; i <= end; i++) {
    const payload = getPayload(actions[i]);
    if (!payload || !payload.type) continue;
    if (payload.type === "click") {
      const { r, c } = payload;
      const rn = typeof r === "number" ? r : parseInt(r, 10);
      const cn = typeof c === "number" ? c : parseInt(c, 10);
      if (rn >= 0 && rn < GRID_SIZE && cn >= 0 && cn < GRID_SIZE) {
        const letter = board[rn][cn];
        if (letter && letter !== "") {
          // Alle cellen die door deze klik zichtbaar worden (zelfde letter overal)
          const cellsBefore = rev.map((row) => row.slice());
          const revAfter = revealAllOfLetter(letter, rev, board);
          const otherCells = [];
          for (let rr = 0; rr < GRID_SIZE; rr++) {
            for (let cc = 0; cc < GRID_SIZE; cc++) {
              if (revAfter[rr][cc] && !cellsBefore[rr][cc] && board[rr][cc] === letter)
                otherCells.push({ r: rr, c: cc });
            }
          }
          // Zelfde volgorde als eigen klik: eerst geklikte cel, dan deel van revealed woord, dan rest (scan)
          const clickedCell = { r: rn, c: cn };
          const clickedIsNew = revAfter[rn][cn] && !cellsBefore[rn][cn] && board[rn][cn] === letter;
          const words = (placements || []).map((p) => p.word);
          const wordsNowVisible = words.filter((w) => isWordFullyVisible(w, placements, revAfter));
          const cellsInRevealedWords = new Set();
          for (const w of wordsNowVisible) {
            const wcells = getWordCells(w, placements);
            for (const { r: cr, c: cc } of wcells) {
              cellsInRevealedWords.add(`${cr},${cc}`);
            }
          }
          const partOfRevealed = otherCells
            .filter(({ r, c }) => (r !== rn || c !== cn) && cellsInRevealedWords.has(`${r},${c}`))
            .sort((a, b) => (a.r !== b.r ? a.r - b.r : a.c - b.c));
          const partSet = new Set(partOfRevealed.map(({ r, c }) => `${r},${c}`));
          const rest = otherCells
            .filter(({ r, c }) => (r !== rn || c !== cn) && !partSet.has(`${r},${c}`))
            .sort((a, b) => (a.r !== b.r ? a.r - b.r : a.c - b.c));
          const cells = [];
          if (clickedIsNew) cells.push(clickedCell);
          for (const cell of partOfRevealed) {
            if (cell.r !== rn || cell.c !== cn) cells.push(cell);
          }
          for (const cell of rest) cells.push(cell);
          if (cells.length === 0 && otherCells.length > 0) {
            otherCells.sort((a, b) => (a.r !== b.r ? a.r - b.r : a.c - b.c));
            for (const cell of otherCells) cells.push(cell);
          }
          steps.push({ type: "click", actionIdx: i, cells });
          rev = revAfter;
        } else {
          steps.push({ type: "click", actionIdx: i, cells: [{ r: rn, c: cn }] });
          rev[rn] = rev[rn].slice();
          rev[rn][cn] = true;
        }
      } else {
        steps.push({ type: "click", actionIdx: i });
      }
    } else if (payload.type === "guess" && payload.revealsLetters) {
      const word = payload.word ? String(payload.word).toUpperCase() : "";
      if (!word) continue;
      const beforeRev = rev.map((row) => row.slice());
      for (let j = 0; j < word.length; j++) {
        rev = revealAllOfLetter(word[j], rev, board);
      }
      const newCells = [];
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          if (rev[r][c] && !beforeRev[r][c]) newCells.push({ r, c });
        }
      }
      newCells
        .sort((a, b) => (a.r !== b.r ? a.r - b.r : a.c - b.c))
        .forEach(({ r, c }, idx) => {
          steps.push({ type: "dict_letter", r, c, isLast: idx === newCells.length - 1 });
        });
      if (newCells.length === 0) steps.push({ type: "guess_mark", word, wrong: true });
    } else if (payload.type === "guess" && payload.correct) {
      const word = payload.word ? String(payload.word).toUpperCase() : "";
      if (!word || guessed.includes(word)) continue;
      const placement = placements.find((p) => String(p.word).toUpperCase() === word);
      const wordCells = placement ? getWordCells(placement.word, placements) : [];
      const newCells = wordCells.filter(({ r, c }) => !rev[r][c]);
      guessed.push(word);
      rev = revealWord(word, placements, rev);
      newCells.forEach(({ r, c }, idx) => {
        steps.push({ type: "word_letter", r, c, word, isLast: idx === newCells.length - 1 });
      });
      if (newCells.length === 0) steps.push({ type: "guess_mark", word });
    } else if (payload.type === "word_solved" && payload.word) {
      const word = payload.word ? String(payload.word).toUpperCase() : "";
      if (!word) continue;
      const placement = placements.find((p) => String(p.word).toUpperCase() === word);
      const wordCells = placement ? getWordCells(placement.word, placements) : [];
      const newCells = wordCells.filter(({ r, c }) => !rev[r][c]);
      rev = revealWord(word, placements, rev);
      for (let j = 0; j < word.length; j++) {
        rev = revealAllOfLetter(word[j], rev, board);
      }
      newCells.forEach(({ r, c }, idx) => {
        steps.push({ type: "word_letter", r, c, word, isLast: idx === newCells.length - 1 });
      });
    }
  }
  return steps;
}

/** Apply one expanded step; returns { rev, guessed }. */
function applyExpandedStep(rev, guessed, step, actions, board, placements) {
  const newRev = rev.map((row) => row.slice());
  let newGuessed = [...guessed];
  const getPayload = (a) => (a.action && typeof a.action === "object" ? a.action : a);
  if (step.type === "click") {
    const payload = actions && actions[step.actionIdx] != null ? getPayload(actions[step.actionIdx]) : null;
    if (payload?.type === "click") {
      const { r, c } = payload;
      const rn = typeof r === "number" ? r : parseInt(r, 10);
      const cn = typeof c === "number" ? c : parseInt(c, 10);
      if (rn >= 0 && rn < GRID_SIZE && cn >= 0 && cn < GRID_SIZE) {
        const letter = board[rn][cn];
        if (letter && letter !== "") {
          return { rev: revealAllOfLetter(letter, newRev, board), guessed: newGuessed };
        }
        newRev[rn] = newRev[rn].slice();
        newRev[rn][cn] = true;
      }
    }
    return { rev: newRev, guessed: newGuessed };
  }
  if (step.type === "word_letter") {
    const { r, c, word, isLast } = step;
    if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
      newRev[r] = newRev[r].slice();
      newRev[r][c] = true;
    }
    if (isLast && word && !newGuessed.includes(word)) newGuessed.push(word);
    return { rev: newRev, guessed: newGuessed };
  }
  if (step.type === "dict_letter") {
    const { r, c } = step;
    if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
      newRev[r] = newRev[r].slice();
      newRev[r][c] = true;
    }
    return { rev: newRev, guessed: newGuessed };
  }
  if (step.type === "guess_mark") {
    if (step.word && !step.wrong && !newGuessed.includes(step.word)) newGuessed.push(step.word);
    return { rev: newRev, guessed: newGuessed };
  }
  return { rev: newRev, guessed: newGuessed };
}

/** Returns [{ r, c }, ...] for the cell(s) that this expanded step reveals (from step data only). */
function getCellsRevealedByStep(step, actions, board) {
  if (!step) return [];
  const getPayload = (a) => (a && a.action && typeof a.action === "object" ? a.action : a);
  if (step.type === "click") {
    if (Array.isArray(step.cells) && step.cells.length > 0)
      return step.cells.filter((cell) => cell.r >= 0 && cell.r < GRID_SIZE && cell.c >= 0 && cell.c < GRID_SIZE);
    if (actions && actions[step.actionIdx] != null) {
      const payload = getPayload(actions[step.actionIdx]);
      if (payload?.type === "click") {
        const rn = typeof payload.r === "number" ? payload.r : parseInt(payload.r, 10);
        const cn = typeof payload.c === "number" ? payload.c : parseInt(payload.c, 10);
        if (rn >= 0 && rn < GRID_SIZE && cn >= 0 && cn < GRID_SIZE) return [{ r: rn, c: cn }];
      }
    }
  }
  if (step.type === "word_letter" && typeof step.r === "number" && typeof step.c === "number") {
    if (step.r >= 0 && step.r < GRID_SIZE && step.c >= 0 && step.c < GRID_SIZE) return [{ r: step.r, c: step.c }];
  }
  if (step.type === "dict_letter" && typeof step.r === "number" && typeof step.c === "number") {
    if (step.r >= 0 && step.r < GRID_SIZE && step.c >= 0 && step.c < GRID_SIZE) return [{ r: step.r, c: step.c }];
  }
  return [];
}

/** Returns all words whose placement includes cell (r, c). */
function getWordsForCell(r, c, placements) {
  const words = [];
  for (const p of placements || []) {
    for (let i = 0; i < p.word.length; i++) {
      let rr = p.r;
      let cc = p.c;
      if (p.dir === "H") cc = p.c + i;
      else if (p.dir === "V") rr = p.r + i;
      else if (p.dir === "DR") { rr = p.r + i; cc = p.c + i; }
      else if (p.dir === "DL") { rr = p.r + i; cc = p.c - i; }
      if (rr === r && cc === c) {
        words.push(String(p.word).toUpperCase());
        break;
      }
    }
  }
  return words;
}

/** Returns true if any cell of the given word has a lastOpponentRevealDelay (animated this turn). */
function isWordAnimatedThisTurn(word, placements, delays) {
  if (!delays || !placements) return false;
  const cells = getWordCells(word, placements);
  if (!cells.length) return false;
  for (const { r, c } of cells) {
    if (delays[r]?.[c] != null) return true;
  }
  return false;
}

function isWordFullyVisible(word, placements, revealed) {
  const placement = placements.find((p) => p.word === word);
  if (!placement) return false;
  for (let i = 0; i < placement.word.length; i++) {
    let rr = placement.r;
    let cc = placement.c;
    if (placement.dir === "H") {
      cc = placement.c + i;
    } else if (placement.dir === "V") {
      rr = placement.r + i;
    } else if (placement.dir === "DR") {
      rr = placement.r + i; cc = placement.c + i;
    } else if (placement.dir === "DL") {
      rr = placement.r + i; cc = placement.c - i;
    }
    if (!revealed[rr][cc]) {
      return false;
    }
  }
  return true;
}

function isCellInFullyVisibleWord(r, c, placements, revealed) {
  for (const p of placements) {
    for (let i = 0; i < p.word.length; i++) {
      let rr = p.r;
      let cc = p.c;
      if (p.dir === "H") {
        cc = p.c + i;
      } else if (p.dir === "V") {
        rr = p.r + i;
      } else if (p.dir === "DR") {
        rr = p.r + i; cc = p.c + i;
      } else if (p.dir === "DL") {
        rr = p.r + i; cc = p.c - i;
      }
      if (rr === r && cc === c) {
        if (isWordFullyVisible(p.word, placements, revealed)) {
          return true;
        }
      }
    }
  }
  return false;
}

function revealEntireBoard(board) {
  return Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, () => true)
  );
}

// ========== FETCH AND BUILD FUNCTIONS ==========

function isPrefillQazx() {
  if (typeof window === "undefined") return false;
  try {
    const rawSearch =
      (window.__WORDHIT_EMBED_QUERY__ || "").replace(/^\?/, "") ||
      (window.location.search || "").replace(/^\?/, "");
    const params = new URLSearchParams(rawSearch);
    return params.get("prefill") === "qazx";
  } catch {
    return false;
  }
}

/**
 * Fetch BattleGame from cache (if loaded) or Parse.
 * When prefill=qazx is in the URL, cache is never used: we always load from Parse BattleGame class.
 */
async function fetchBattleGameFromParse(battleGameId, langString) {
  const forceParse = isPrefillQazx();

  if (!forceParse) {
    const cached = getBattleGameFromCache(battleGameId, langString);
    if (cached) {
      return cached;
    }
  } else {
    // prefill=qazx: intentionally skip cache, always use Parse BattleGame class
    console.log("[WordHitBattle] prefill=qazx: loading BattleGame from Parse (battleGameId:", battleGameId, ")");
  }

  if (!Parse || !Parse.Object) {
    throw new Error("Parse is not ready. Cannot load battle game.");
  }

  try {
    const result = await Parse.Cloud.run("getBattleGame", {
      battleGameId: battleGameId,
    });

    if (!result || !result.success) {
      throw new Error(
        langString === "nl"
          ? "Battle game niet gevonden."
          : "Battle game not found."
      );
    }

    const theme = langString === "nl" ? result.themeNL : result.theme;
    const words = langString === "nl" ? result.wordsNL : result.words;
    const idNumber = result.idNumber || 1;

    if (!theme || !words || !Array.isArray(words)) {
      throw new Error(
        langString === "nl"
          ? "Battle game data ongeldig."
          : "Invalid battle game data."
      );
    }

    return {
      theme,
      words,
      idNumber,
      battleGameId: result.battleGameId,
    };
  } catch (error) {
    console.error("[WordHitBattle] Error fetching battle game:", error);
    throw error;
  }
}

/**
 * Build game board from puzzle (same logic as buildGameForToday in WordHit.jsx)
 */
function buildGameFromPuzzle(puzzle, battleGameId) {
  const words = puzzle.words.map((w) => String(w).toUpperCase());
  
  // Use battleGameId as seed for deterministic board generation
  // Convert battleGameId string to number for seed
  let seedNum = 1;
  if (battleGameId) {
    // Hash the battleGameId string to a number
    let hash = 0;
    for (let i = 0; i < battleGameId.length; i++) {
      const char = battleGameId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    seedNum = Math.abs(hash);
  }
  
  const rng = mulberry32(seedNum);

  let placementsResult = null;
  const sortedWords = [...words].sort((a, b) => b.length - a.length);

  const planOptions = [
    [
      [["DR", "DL"], ["H"], ["V"]],
      [["V"], ["H"], ["DR", "DL"]],
      [["H"], ["DR", "DL"], ["V"]],
    ],
    [
      [["DR", "DL"], ["V"], ["H"]],
      [["V"], ["H"], ["DR", "DL"]],
      [[ALL_DIRECTIONS]],
    ],
    [
      [["V"], ["DR", "DL"], ["H"]],
      [["DR", "DL"], ["H"], ["V"]],
      [[ALL_DIRECTIONS]],
    ],
    [
      [[ALL_DIRECTIONS]],
    ],
  ];

  // First pass: require diagonal + vertical + cross when possible.
  for (const plan of planOptions) {
    const expandedPlans = expandPlans(plan, sortedWords.length);
    placementsResult = tryPlaceWordsWithPlans(sortedWords, rng, expandedPlans, {
      requireVertical: true,
      requireCross: true,
      requireDiagonal: true,
    });
    if (placementsResult) break;
  }

  // Second pass: require diagonal + vertical, cross optional.
  if (!placementsResult) {
    for (const plan of planOptions) {
      const expandedPlans = expandPlans(plan, sortedWords.length);
      placementsResult = tryPlaceWordsWithPlans(sortedWords, rng, expandedPlans, {
        requireVertical: true,
        requireCross: false,
        requireDiagonal: true,
      });
      if (placementsResult) break;
    }
  }

  // Third pass: require vertical + cross.
  if (!placementsResult) {
    for (const plan of planOptions) {
      const expandedPlans = expandPlans(plan, sortedWords.length);
      placementsResult = tryPlaceWordsWithPlans(sortedWords, rng, expandedPlans, {
        requireVertical: true,
        requireCross: true,
        requireDiagonal: false,
      });
      if (placementsResult) break;
    }
  }

  // Fourth pass: require vertical only.
  if (!placementsResult) {
    for (const plan of planOptions) {
      const expandedPlans = expandPlans(plan, sortedWords.length);
      placementsResult = tryPlaceWordsWithPlans(sortedWords, rng, expandedPlans, {
        requireVertical: true,
        requireCross: false,
        requireDiagonal: false,
      });
      if (placementsResult) break;
    }
  }

  const board = placementsResult ? placementsResult.board : createEmptyBoard();
  const placements = placementsResult ? placementsResult.placements : [];

  return {
    board,
    theme: puzzle.theme,
    words: words,
    placements,
    idNumber: puzzle.idNumber,
    battleGameId: puzzle.battleGameId,
  };
}

// ========== MAIN COMPONENT ==========

function WordHitBattle({ battleGameId, battleInfo, onClose, onNavigateToNewBattle, langString = "en" }) {
  const [game, setGame] = useState(null);
  const gameRef = useRef(null); // Always points to the latest game (avoids async race issues)
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [revealed, setRevealed] = useState(
    Array.from({ length: GRID_SIZE }, () =>
      Array.from({ length: GRID_SIZE }, () => false)
    )
  );
  const [guess, setGuess] = useState("");
  const [guessedWords, setGuessedWords] = useState([]);
  const [wrongGuessedWords, setWrongGuessedWords] = useState([]);
  const [guessShake, setGuessShake] = useState(false);
  const [guessFeedback, setGuessFeedback] = useState("");
  const guessFeedbackTimerRef = useRef(null);
  const [showWrongWordsModal, setShowWrongWordsModal] = useState(false);
  const [hasWon, setHasWon] = useState(false);
  const [hasLost, setHasLost] = useState(false);
  const [showCheckOverlay, setShowCheckOverlay] = useState(false);
  const [showCrossOverlay, setShowCrossOverlay] = useState(false);
  const [gameResult, setGameResult] = useState(null); // { winnerId, winnerName, loserId, loserName, winnerScore, loserScore } - kept for potential future use
  const [isNextTransitioning, setIsNextTransitioning] = useState(false);
  const [isSavingGameResult, setIsSavingGameResult] = useState(false); // Track if we're saving game result (for UI updates)
  // Track total discovered words across the entire battle (all games)
  const [battleTotalWords, setBattleTotalWords] = useState(null);
  // Battle winner (first to BATTLE_WIN_WORD_COUNT words); updated on refresh so we see it after saving
  const [refreshedBattleWinnerId, setRefreshedBattleWinnerId] = useState(null);
  // Language from server (getUserBattles refresh) so opponent always gets battle language even if list was stale
  const [refreshedBattleLanguage, setRefreshedBattleLanguage] = useState(null);
  // Popup when battle is finished (won/lost)
  const [showBattleFinishedModal, setShowBattleFinishedModal] = useState(false);
  const pendingBattleFinishedModalRef = useRef(false);
  // Confetti when current user wins the battle
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiTimeoutRef = useRef(null);
  const battleWinTimeoutRef = useRef(null);
  // Turn-based system: track actions per turn
  const [actionsThisTurn, setActionsThisTurn] = useState(0);
  // Get current user ID
  const currentUserId = currentUser?.id || null;
  const opponentId = battleInfo?.opponent?.id || null;
  // Game content (theme, words) uses the battle's language; prefer server-fetched language so opponent gets it too
  const effectiveLangString = (refreshedBattleLanguage === "nl" || refreshedBattleLanguage === "en")
    ? refreshedBattleLanguage
    : (battleInfo?.language === "nl" || battleInfo?.language === "en")
      ? battleInfo.language
      : (langString === "nl" ? "nl" : "en");
  // Helper: prefer refreshed totals from state, fallback to battleInfo
  const battleTotals = battleTotalWords || battleInfo?.totalDiscoveredWords || null;
  // Track whose turn it is (User ID, not number)
  const [turnPlayerId, setTurnPlayerId] = useState(null); // Will be set from Parse or default to current user
  const turnPlayerIdRef = useRef(null); // Keep ref in sync so we can block clicks before state updates
  const actionsThisTurnRef = useRef(0); // Same: block "no more actions" before state updates
  const MAX_ACTIONS_PER_TURN = 2;
  // PlaySequence tracking (with player info)
  const [playSequenceActions, setPlaySequenceActions] = useState([]);
  const playSequenceActionsRef = useRef([]);
  // Per-click lokale reveal-delays voor cells van dezelfde letter (voor flip-animatie bij eigen klik)
  const [localRevealDelays, setLocalRevealDelays] = useState(null);
  // Pas helder (activated) zodra de flip-animatie begint (eigen klik)
  const [cellsWithFlipStarted, setCellsWithFlipStarted] = useState(() => new Set());
  const flipStartedTimeoutsRef = useRef([]);
  // Pas helder (activated) zodra de replay-flip begint (auto replay)
  const [replayCellsWithFlipStarted, setReplayCellsWithFlipStarted] = useState(() => new Set());
  const replayFlipStartedTimeoutsRef = useRef([]);
  // Bij correcte word guess (current user): houd woord blauw tot laatste flip klaar is
  const [pendingSolvedWords, setPendingSolvedWords] = useState([]);
  const pendingSolvedWordTimersRef = useRef({});
  // Lokale animatie (eigen klik/guess): tijdens deze duur geen nieuwe acties toelaten
  const [isLocalAnimating, setIsLocalAnimating] = useState(false);
  const localAnimationTimeoutRef = useRef(null);
  // Replay laatste tegenstander-beurt: stap-index (0 = toon state vóór die beurt, 1..n = na actie 1..n)
  const [opponentReplayStep, setOpponentReplayStep] = useState(0);
  const [replayRestartKey, setReplayRestartKey] = useState(0);
  // Flash alle reeds gerevealde letters/woorden kort bij klik buiten de grid
  const [flashRevealed, setFlashRevealed] = useState(false);
  const lastPlaySequenceLengthRef = useRef(0); // Track last known length for polling
  /** Bij eerste load preloaden we playSequence in loadBattleGame; dan hoeft de useEffect geen fetch te doen. */
  const skipNextPlaySequenceFetchRef = useRef(false);
  const isSavingTurnRef = useRef(false); // Track if we're currently saving a turn change
  const hasAutoAdvancedRef = useRef(false);
  const autoAdvanceFallbackTimeoutRef = useRef(null);

  function startLocalAnimation(durationMs) {
    const d = Math.max(0, durationMs || 0);

    if (localAnimationTimeoutRef.current) {
      clearTimeout(localAnimationTimeoutRef.current);
      localAnimationTimeoutRef.current = null;
    }

    if (d === 0) {
      setIsLocalAnimating(false);
      return;
    }

    setIsLocalAnimating(true);
    localAnimationTimeoutRef.current = setTimeout(() => {
      setIsLocalAnimating(false);
      localAnimationTimeoutRef.current = null;
    }, d);
  }

  // Unieke key per battle-game voor lokale opslag van foute woorden
  const battleWrongKey = useMemo(() => {
    if (!game) return null;
    if (game.battleGameId != null) {
      return String(game.battleGameId);
    }
    if (game.idNumber != null) {
      return `id-${game.idNumber}`;
    }
    return null;
  }, [game?.battleGameId, game?.idNumber]);
  useEffect(() => {
    console.log("[WordHitBattle][WrongWords] key", {
      battleWrongKey,
      battleGameId: game?.battleGameId ?? null,
      idNumber: game?.idNumber ?? null,
    });
  }, [battleWrongKey, game?.battleGameId, game?.idNumber]);
  const battleWrongKeyRef = useRef(null);
  // Keep ref in sync during render so it's available immediately (also before effects run)
  battleWrongKeyRef.current = battleWrongKey;

  // Restore wrong guessed words per battle game on load
  useEffect(() => {
    if (!battleWrongKey) return;
    const stored = getStoredBattleWrongWords(battleWrongKey);
    if (stored && Array.isArray(stored)) {
      setWrongGuessedWords(stored);
    } else {
      setWrongGuessedWords([]);
      console.log("[WordHitBattle][WrongWords] restore-empty", { key: battleWrongKey, storedType: typeof stored });
    }
  }, [battleWrongKey]);
  const isSavingGameResultRef = useRef(false); // Track if we're currently saving game result (winner)
  const refreshBattleTotalsRef = useRef(null); // Ref to refreshBattleTotals function
  const battleSaveInFlightRef = useRef(false);
  const battleSavePendingRef = useRef(false);
  const battleSavePendingTurnRef = useRef(null);
  const battleSavePendingWinnerRef = useRef(null);
  const battleSavePendingActionsRef = useRef(null); // bij queue: acties meenemen zodat laatste guess niet verloren gaat
  /** Debounce: na actie even wachten zodat snelle tweede klik in één save gaat. */
  const battleSaveDebounceTimeoutRef = useRef(null);
  const BATTLE_SAVE_DEBOUNCE_MS = 280;
  /** Na save: push sturen als we net beurt aan tegenstander gaven (wordt in debounced save gecheckt). */
  const pendingPushAfterSaveRef = useRef(false);
  /** Skip poll for this long after a turn save completed (ms) so server state is not stale */
  const lastTurnSaveCompletedAtRef = useRef(0);
  const POLL_PAUSE_AFTER_TURN_SAVE_MS = 3000;
  /** Only send one turn push per turn change (guards against Strict Mode double-invoke). */
  const lastTurnPushSentRef = useRef({ battleId: null, turnPlayerId: null });
  /** Volledige playSequence (keyed by game id) – enige bron voor beurt. Bij load/poll/add actie bijwerken. */
  const playSequenceObjectRef = useRef({});
  /** Only track battle_finished to Amplitude once per battle. */
  const battleFinishedAmplitudeLoggedRef = useRef(new Set());

  // Swipe gestures to close battle (down)
  const battleSwipeRef = useRef({ startX: 0, startY: 0 });
  const BATTLE_SWIPE_MIN_DISTANCE = 60;
  const handleBattleTouchStart = useCallback((e) => {
    const t = e.touches?.[0];
    if (t) {
      battleSwipeRef.current = { startX: t.clientX, startY: t.clientY };
    }
  }, []);
  const handleBattleTouchEnd = useCallback(
    (e) => {
      const t = e.changedTouches?.[0];
      if (!t) return;
      const { startX, startY } = battleSwipeRef.current;
      const deltaX = t.clientX - startX;
      const deltaY = t.clientY - startY;

      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      // Vertical swipe down to close
      if (absY >= BATTLE_SWIPE_MIN_DISTANCE && absY > absX && deltaY > 0) {
        onClose();
        return;
      }
    },
    [onClose]
  );

  // Offline: show warning banner and deactivate game when no internet
  const [isOffline, setIsOffline] = useState(
    () => (typeof navigator !== "undefined" ? !navigator.onLine : false)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Battle chat: modal visibility, messages from battleInfo (or local after send), and unread indicator
  const [isRematching, setIsRematching] = useState(false);
  const rematchInFlightRef = useRef(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLastReadAt, setChatLastReadAt] = useState({});
  const [chatSending, setChatSending] = useState(false);
  const hasChatUnread = useMemo(() => {
    const lastRead = chatLastReadAt[currentUserId];
    if (!lastRead) return chatMessages.some((m) => m.senderId !== currentUserId);
    return chatMessages.some(
      (m) => m.senderId !== currentUserId && new Date(m.createdAt) > new Date(lastRead)
    );
  }, [chatMessages, chatLastReadAt, currentUserId]);

  const battleOrderForNext = useMemo(
    () => (Array.isArray(battleInfo?.battleOrder) ? battleInfo.battleOrder : []),
    [battleInfo?.battleOrder]
  );
  const currentGameIndexInOrder = game?.idNumber != null ? battleOrderForNext.indexOf(game.idNumber) : -1;
  const hasNextGameInBattleOrder =
    currentGameIndexInOrder >= 0 && currentGameIndexInOrder + 1 < battleOrderForNext.length;
  const battleWinnerId = battleInfo?.battleWinnerId ?? refreshedBattleWinnerId ?? null;
  const battleIsOver = !!battleWinnerId;
  const battleWinReachedLocal =
    !!battleTotals &&
    typeof battleTotals === "object" &&
    !Array.isArray(battleTotals) &&
    ((battleTotals[currentUserId] ?? 0) >= BATTLE_WIN_WORD_COUNT ||
      (battleTotals[opponentId] ?? 0) >= BATTLE_WIN_WORD_COUNT);
  const battleIsOverForUI = battleIsOver || battleWinReachedLocal;
  const battleWinnerIdForUI = useMemo(() => {
    if (battleWinnerId) return battleWinnerId;
    if (!battleWinReachedLocal) return null;
    if (!battleTotals || typeof battleTotals !== "object" || Array.isArray(battleTotals)) return null;
    const myScore = battleTotals[currentUserId] ?? 0;
    const oppScore = battleTotals[opponentId] ?? 0;
    // First to reach the threshold wins; if tie (rare), current user wins (first to finish).
    if (myScore >= BATTLE_WIN_WORD_COUNT && oppScore >= BATTLE_WIN_WORD_COUNT) return currentUserId;
    if (myScore >= BATTLE_WIN_WORD_COUNT) return currentUserId;
    if (oppScore >= BATTLE_WIN_WORD_COUNT) return opponentId;
    return null;
  }, [battleWinnerId, battleWinReachedLocal, battleTotals, currentUserId, opponentId]);

  const handleSendChatMessage = useCallback(
    async (text) => {
      if (!battleInfo?.id || !text?.trim()) return;
      setChatSending(true);
      try {
        const result = await Parse.Cloud.run("sendBattleChatMessage", {
          battleId: battleInfo.id,
          text: text.trim(),
        });
        if (result?.success && Array.isArray(result.chatMessages)) {
          setChatMessages(result.chatMessages);
        }
      } catch (err) {
        console.warn("[WordHitBattle] sendBattleChatMessage failed:", err);
      } finally {
        setChatSending(false);
      }
    },
    [battleInfo?.id]
  );

  const handleMarkChatRead = useCallback(async () => {
    if (!battleInfo?.id || !currentUserId) return;
    const now = new Date().toISOString();
    setChatLastReadAt((prev) => ({ ...prev, [currentUserId]: now }));
    try {
      await Parse.Cloud.run("markBattleChatRead", { battleId: battleInfo.id });
    } catch (err) {
      console.warn("[WordHitBattle] markBattleChatRead failed:", err);
    }
  }, [battleInfo?.id, currentUserId]);

  const handleRematchSameOpponent = useCallback(async () => {
    if (!opponentId || typeof onNavigateToNewBattle !== "function") return;
    if (isOffline || rematchInFlightRef.current) return;
    rematchInFlightRef.current = true;
    setIsRematching(true);
    try {
      const langForBattle = effectiveLangString === "nl" ? "nl" : "en";
      const battleResult = await startBattleWithOpponent(opponentId, langForBattle);
      if (!battleResult?.success || !(battleResult.userBattleId || battleResult.battleId)) {
        console.error("[WordHitBattle] startBattle failed:", battleResult);
        return;
      }
      const newBattleId = battleResult.userBattleId || battleResult.battleId;
      const battlesResult = await Parse.Cloud.run("getUserBattles");
      if (!battlesResult?.success || !Array.isArray(battlesResult.battles)) {
        console.warn("[WordHitBattle] Rematch: getUserBattles failed");
        return;
      }
      const found = battlesResult.battles.find(
        (b) => b.id === newBattleId || b.objectId === newBattleId
      );
      if (!found) {
        console.warn("[WordHitBattle] Rematch: new battle not in list yet", newBattleId);
        return;
      }
      const nextGameId = found.battleGameId || newBattleId;
      onNavigateToNewBattle(nextGameId, found);
    } catch (err) {
      console.error("[WordHitBattle] Rematch error:", err);
    } finally {
      rematchInFlightRef.current = false;
      setIsRematching(false);
    }
  }, [opponentId, onNavigateToNewBattle, isOffline, effectiveLangString]);

  // Action format: new = { user, idNumber, action }; old = { player, type, ... }. Helpers support both.
  function getActionPlayerId(action) {
    if (!action) return null;
    if (action.user != null) {
      const u = action.user;
      if (typeof u === "object" && u !== null) return u.id ?? u.objectId ?? String(u);
      return String(u);
    }
    return action.player != null ? String(action.player) : null;
  }
  function getActionPayload(action) {
    if (!action) return action;
    return action.action && typeof action.action === "object" ? action.action : action;
  }
  function isCorrectGuessBy(action, playerId) {
    const p = getActionPayload(action);
    return p && p.type === "guess" && p.correct === true && getActionPlayerId(action) === playerId;
  }
  /** +1 battle score: correct guess or word fully revealed by click (word_solved) */
  function isWordScoredBy(action, playerId) {
    const p = getActionPayload(action);
    const id = getActionPlayerId(action);
    if (id !== playerId) return false;
    return (p && p.type === "guess" && p.correct === true) || (p && p.type === "word_solved");
  }
  /** Only "click" and "guess" consume a turn; "word_solved" is a consequence of a click and must not count. */
  function isTurnConsumingAction(action) {
    const p = getActionPayload(action);
    return p && (p.type === "click" || p.type === "guess");
  }

  /**
   * Beurt puur uit acties: onderaan 2 acties tegenstander → ik aan beurt;
   * onderaan 1 actie tegenstander → tegenstander nog 1 over; onderaan 2 van mij → tegenstander.
   * previousActionsWhenEmpty: vorige game's acties – gebruikt bij lege current én als carry-over (starter had 1 over → na 1 actie hier = 2 → wissel).
   */
  function getTurnFromPlaySequence(currentActions, previousActionsWhenEmpty, currentUserId, opponentId) {
    const actions = Array.isArray(currentActions) ? currentActions : [];
    const prev = Array.isArray(previousActionsWhenEmpty) ? previousActionsWhenEmpty : [];
    const derivedFromPrev = prev.length > 0 ? deriveTurnFromPlaySequence(prev, currentUserId, opponentId) : null;

    if (actions.length > 0) {
      const derived = deriveTurnFromPlaySequence(actions, currentUserId, opponentId);
      if (derived) {
        let actionsThisTurn = derived.actionsThisTurn;
        // Carry-over alleen bij de allereerste actie van deze game: sequence heeft precies 1 turn-actie, en vorige game eindigde met die speler 1/2
        const turnConsumingCount = actions.filter(a => isTurnConsumingAction(a)).length;
        if (
          turnConsumingCount === 1 &&
          derivedFromPrev &&
          derived.turnPlayerId === derivedFromPrev.turnPlayerId &&
          derivedFromPrev.actionsThisTurn > 0
        ) {
          actionsThisTurn = derived.actionsThisTurn + derivedFromPrev.actionsThisTurn;
        }
        const turnPlayerId = actionsThisTurn >= 2
          ? (derived.turnPlayerId === currentUserId ? opponentId : currentUserId)
          : derived.turnPlayerId;
        const finalActionsThisTurn = actionsThisTurn >= 2 ? 0 : actionsThisTurn;
        return { turnPlayerId, actionsThisTurn: finalActionsThisTurn };
      }
    }
    // Lege sequence: wie start = afleiden uit previousActionsWhenEmpty
    if (!derivedFromPrev) return { turnPlayerId: currentUserId, actionsThisTurn: 0 };
    return { turnPlayerId: derivedFromPrev.turnPlayerId, actionsThisTurn: derivedFromPrev.actionsThisTurn };
  }

  /** Haalt current + previous actions uit refs. Previous altijd meegeven als er een vorige game is (nodig voor carry-over na eerste actie). */
  function getCurrentAndPreviousActions() {
    const current = playSequenceActionsRef.current ?? [];
    const obj = playSequenceObjectRef.current;
    const order = Array.isArray(battleInfo?.battleOrder) ? battleInfo.battleOrder : [];
    const gameId = game?.idNumber;
    const idx = gameId != null ? order.indexOf(gameId) : -1;
    const prevActions = idx > 0 && obj ? (obj[String(order[idx - 1])]?.actions ?? null) : null;
    return { currentActions: current, previousActionsWhenEmpty: prevActions };
  }

  function getPlaySequenceArray(playSequence, idNumber) {
    if (Array.isArray(playSequence)) return playSequence;
    if (playSequence && typeof playSequence === "object" && !Array.isArray(playSequence)) {
      const key = idNumber != null ? String(idNumber) : null;
      if (key && playSequence[key] && Array.isArray(playSequence[key].actions)) return playSequence[key].actions;
      const firstKey = Object.keys(playSequence)[0];
      if (firstKey && playSequence[firstKey] && Array.isArray(playSequence[firstKey].actions)) return playSequence[firstKey].actions;
    }
    return [];
  }

  function deriveTurnFromPlaySequence(playSequence, currentUserId, opponentId) {
    const actions = Array.isArray(playSequence) ? playSequence : [];
    if (actions.length === 0) return null;
    let lastPlayerId = null;
    let countAtEnd = 0;
    for (let i = actions.length - 1; i >= 0; i--) {
      const a = actions[i];
      if (!isTurnConsumingAction(a)) continue;
      const pid = getActionPlayerId(a);
      if (lastPlayerId == null) {
        lastPlayerId = pid;
        countAtEnd = 1;
      } else if (pid === lastPlayerId) {
        countAtEnd++;
      } else {
        break;
      }
    }
    if (lastPlayerId == null) return null;
    if (countAtEnd >= 2) {
      return { turnPlayerId: lastPlayerId === currentUserId ? opponentId : currentUserId, actionsThisTurn: 0 };
    }
    return { turnPlayerId: lastPlayerId, actionsThisTurn: countAtEnd };
  }

  /** Beurt puur uit actie-arrays. Na elke actie / load / poll opnieuw aanroepen. */
  const derivedTurnForUI = useMemo(() => {
    const { currentActions, previousActionsWhenEmpty } = getCurrentAndPreviousActions();
    return getTurnFromPlaySequence(currentActions, previousActionsWhenEmpty, currentUserId, opponentId);
  }, [playSequenceActions, game?.idNumber, battleInfo?.battleOrder, currentUserId, opponentId]);

  // Turn-state voor push e.d.; afgeleid uit playSequence
  useEffect(() => {
    if (!game?.idNumber || !battleInfo?.id) return;
    const { currentActions, previousActionsWhenEmpty } = getCurrentAndPreviousActions();
    const turn = getTurnFromPlaySequence(currentActions, previousActionsWhenEmpty, currentUserId, opponentId);
    setTurnPlayerId(turn.turnPlayerId);
    turnPlayerIdRef.current = turn.turnPlayerId;
    setActionsThisTurn(turn.actionsThisTurn);
    actionsThisTurnRef.current = turn.actionsThisTurn;
    if (turn.turnPlayerId === currentUserId) lastTurnPushSentRef.current = { battleId: null, turnPlayerId: null };
  }, [playSequenceActions, game?.idNumber, battleInfo?.id, battleInfo?.battleOrder, currentUserId, opponentId]);

  // Keep gameRef in sync with the latest game (must run whenever `game` changes)
  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  // Helper function to refresh totalDiscoveredWords from server
  const refreshBattleTotals = useCallback(async () => {
    if (!battleInfo?.id) return;
    try {
      const result = await Parse.Cloud.run("getUserBattles");
      if (result && result.success && Array.isArray(result.battles)) {
        const updatedBattle = result.battles.find(
          (b) => b.id === battleInfo.id || b.objectId === battleInfo.id
        );
        if (updatedBattle) {
          if (
            updatedBattle.totalDiscoveredWords &&
            typeof updatedBattle.totalDiscoveredWords === "object" &&
            !Array.isArray(updatedBattle.totalDiscoveredWords)
          ) {
            const serverTotals = updatedBattle.totalDiscoveredWords;
            // Merge server totals with any locally optimistic totals, never decreasing counts
            setBattleTotalWords(prev => {
              if (!prev || typeof prev !== "object" || Array.isArray(prev)) {
                return serverTotals;
              }
              const merged = { ...serverTotals };
              for (const [playerId, prevCount] of Object.entries(prev)) {
                if (typeof prevCount !== "number") continue;
                const serverCount = typeof merged[playerId] === "number" ? merged[playerId] : 0;
                if (prevCount > serverCount) {
                  merged[playerId] = prevCount;
                }
              }
              return merged;
            });
          }
          if (updatedBattle.battleWinnerId != null) {
            setRefreshedBattleWinnerId(updatedBattle.battleWinnerId);
          }
          // So opponent (and creator) always use battle language from server
          if (updatedBattle.language === "nl" || updatedBattle.language === "en") {
            setRefreshedBattleLanguage(updatedBattle.language);
          }
          if (Array.isArray(updatedBattle.chatMessages)) {
            setChatMessages(updatedBattle.chatMessages);
          }
          if (updatedBattle.chatLastReadAt && typeof updatedBattle.chatLastReadAt === "object") {
            setChatLastReadAt(updatedBattle.chatLastReadAt);
          }
        }
      }
    } catch (error) {
      console.warn("[WordHitBattle] Failed to refresh totalDiscoveredWords:", error);
    }
  }, [battleInfo?.id]);

  // Keep ref in sync with refreshBattleTotals function
  useEffect(() => {
    refreshBattleTotalsRef.current = refreshBattleTotals;
  }, [refreshBattleTotals]);

  // Refresh totalDiscoveredWords from server when battle loads or finishes
  useEffect(() => {
    void refreshBattleTotals();
  }, [battleInfo?.id, hasWon, hasLost, refreshBattleTotals]);

  // Sync battle language from prop when we have it (avoids extra reload when list already had language)
  useEffect(() => {
    if (battleInfo?.language === "nl" || battleInfo?.language === "en") {
      setRefreshedBattleLanguage(battleInfo.language);
    }
  }, [battleInfo?.language]);

  // Sync chat from battleInfo when it loads or is refreshed
  useEffect(() => {
    if (battleInfo?.chatMessages && Array.isArray(battleInfo.chatMessages)) {
      setChatMessages(battleInfo.chatMessages);
    }
    if (battleInfo?.chatLastReadAt && typeof battleInfo.chatLastReadAt === "object") {
      setChatLastReadAt(battleInfo.chatLastReadAt);
    }
  }, [battleInfo?.id, battleInfo?.chatMessages, battleInfo?.chatLastReadAt]);

  // Chat modal: poll for new messages while open (immediate refresh on open, then every CHAT_POLL_INTERVAL_MS)
  useEffect(() => {
    if (!showChatModal || !battleInfo?.id) return;
    void refreshBattleTotals();
    const intervalId = setInterval(() => {
      if (refreshBattleTotalsRef.current) void refreshBattleTotalsRef.current();
    }, CHAT_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [showChatModal, battleInfo?.id, refreshBattleTotals]);

  // Show finished popup when battle has a winner (once; user can close it).
  // Wacht tot lokale animatie klaar is en daarna nog 1.5s voordat popup/confetti verschijnen,
  // zodat de laatste flip van de winnende woord-animatie niet wordt onderbroken.
  useEffect(() => {
    const winnerId = battleInfo?.battleWinnerId ?? refreshedBattleWinnerId;
    const currentUserId = currentUser?.id || null;
    if (winnerId) {
      const battleId = battleInfo?.id;
      if (battleId && !battleFinishedAmplitudeLoggedRef.current.has(battleId)) {
        battleFinishedAmplitudeLoggedRef.current.add(battleId);
        try {
          amplitude.track("battle_finished", {
            battle_id: battleId,
            winner_id: winnerId,
            current_user_won: winnerId === currentUserId || String(winnerId) === String(currentUserId),
          });
        } catch (amplitudeError) {
          console.warn("Failed to track battle_finished:", amplitudeError);
        }
      }
      if (battleWinTimeoutRef.current) {
        clearTimeout(battleWinTimeoutRef.current);
        battleWinTimeoutRef.current = null;
      }

      const showWin = () => {
        if (winnerId === currentUserId || String(winnerId) === String(currentUserId)) {
          setShowConfetti(true);
          if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
            confettiTimeoutRef.current = null;
          }
          confettiTimeoutRef.current = setTimeout(() => {
            setShowConfetti(false);
            confettiTimeoutRef.current = null;
          }, 10000);
        }
      };

      const waitForAnimationThenShow = () => {
        if (isLocalAnimating) {
          battleWinTimeoutRef.current = setTimeout(waitForAnimationThenShow, 200);
        } else {
          battleWinTimeoutRef.current = setTimeout(showWin, 1500);
        }
      };

      waitForAnimationThenShow();
      return () => {
        if (battleWinTimeoutRef.current) {
          clearTimeout(battleWinTimeoutRef.current);
          battleWinTimeoutRef.current = null;
        }
      };
    } else {
      setShowBattleFinishedModal(false);
      setShowConfetti(false);
      if (confettiTimeoutRef.current) {
        clearTimeout(confettiTimeoutRef.current);
        confettiTimeoutRef.current = null;
      }
      if (battleWinTimeoutRef.current) {
        clearTimeout(battleWinTimeoutRef.current);
        battleWinTimeoutRef.current = null;
      }
    }
  }, [battleInfo?.battleWinnerId, refreshedBattleWinnerId, currentUser?.id, battleInfo?.id, isLocalAnimating]);

  useEffect(() => {
    // Get current user
    const user = Parse.User.current();
    setCurrentUser(user);
    
    // Get opponent from battleInfo
    if (battleInfo && battleInfo.opponent) {
      setOpponent(battleInfo.opponent);
    }
    
    loadBattleGame();
  }, [battleGameId, langString, battleInfo, refreshedBattleLanguage]);

  useEffect(() => {
    ensureDictionaryLoaded(effectiveLangString).catch((error) => {
      console.warn("Failed to load word dictionary", error);
    });
  }, [effectiveLangString]);

  // Request push notification permission when first battle is loaded
  useEffect(() => {
    if (!game || !battleInfo?.id) return;
    
    // Only request if we haven't asked before
    if (!hasAskedForPushPermission()) {
      console.log("[WordHitBattle] First battle loaded, requesting push notification permission...");
      requestPushNotificationPermission().then(result => {
        if (result.granted) {
          console.log("[WordHitBattle] Push notification permission granted");
        } else {
          console.log("[WordHitBattle] Push notification permission denied:", result.error);
        }
      }).catch(error => {
        console.error("[WordHitBattle] Error requesting push notification permission:", error);
      });
    }
  }, [game, battleInfo?.id]); // Only run when game is first loaded

  // Poll every 5 seconds while battle is in view – always sync with server
  useEffect(() => {
    if (!battleInfo?.id || !game || hasWon || hasLost) return;

    const pollInterval = setInterval(async () => {
      try {
        if (battleSaveInFlightRef.current) return;
        if (Date.now() - lastTurnSaveCompletedAtRef.current < POLL_PAUSE_AFTER_TURN_SAVE_MS) return;
        const result = await fetchBattlePlaySequenceFromParse(battleInfo.id, game.idNumber);
        if (!result) return;
        const remotePlaySequence = Array.isArray(result.playSequence) ? result.playSequence : [];
        const remoteLength = remotePlaySequence.length;
        const localLength = playSequenceActionsRef.current.length;

        // Alleen overschrijven als server meer acties heeft (tegenstander heeft gezet). Niet overschrijven als server korter is – dan zou de laatste guess/winner kunnen verdwijnen.
        if (remoteLength > localLength) {
          // Volledige playSequence voor beurt-afleiding (vorige game voor "wie start")
          try {
            const fullResult = await Parse.Cloud.run("getBattlePlaySequenceObject", { userBattleId: battleInfo.id });
            if (fullResult?.success && fullResult?.playSequence) {
              playSequenceObjectRef.current = fullResult.playSequence;
            }
          } catch (e) {
            console.warn("[WordHitBattle] Poll: getBattlePlaySequenceObject failed", e);
          }
          applyPlaySequenceActions(remotePlaySequence, null);
          setPlaySequenceActions(remotePlaySequence);
          playSequenceActionsRef.current = remotePlaySequence;
          lastPlaySequenceLengthRef.current = remoteLength;
        }

        /* Chat en battle totals elke 5 sec meenemen (ook als er geen nieuwe game-actions zijn) */
        if (refreshBattleTotalsRef.current) await refreshBattleTotalsRef.current();

        if (result.winner && !hasWon && !hasLost) {
          const playerScores = result.playerScores ?? {};
          let currentUserScore = playerScores[currentUserId] ?? 0;
          let opponentScore = playerScores[opponentId] ?? 0;
          if (Object.keys(playerScores || {}).length === 0) {
            currentUserScore = remotePlaySequence.filter(a => isWordScoredBy(a, currentUserId)).length;
            opponentScore = remotePlaySequence.filter(a => isWordScoredBy(a, opponentId)).length;
          }
          const winnerId = result.winner;
          const loserId = winnerId === currentUserId ? opponentId : currentUserId;
          const winnerScore = winnerId === currentUserId ? currentUserScore : opponentScore;
          const loserScore = winnerId === currentUserId ? opponentScore : currentUserScore;
          const winnerName = winnerId === currentUserId ? (currentUser?.get("name") || "Jij") : (battleInfo?.opponent?.name || "Tegenstander");
          const loserName = loserId === currentUserId ? (currentUser?.get("name") || "Jij") : (battleInfo?.opponent?.name || "Tegenstander");
          setGameResult({ winnerId, winnerName, loserId, loserName, winnerScore, loserScore });
          if (winnerId === currentUserId) setHasWon(true);
          else setHasLost(true);
        }
      } catch (error) {
        console.error("[WordHitBattle] Poll error:", error);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [battleInfo?.id, game, game?.idNumber, hasWon, hasLost, opponentId]);

  // Log game info only when game changes (not on every render)
  useEffect(() => {
    if (game) {
      console.log("[WordHitBattle] Game loaded:", {
        theme: game.theme,
        words: game.words,
        boardSize: game.board.length,
        placements: game.placements?.length || 0,
      });
    }
  }, [game?.theme, game?.words?.length]); // Only log when game data actually changes

  // Load and apply playSequence to board when game is loaded
  useEffect(() => {
    if (!game || !battleInfo?.id) return;

    // Als we in loadBattleGame de playSequence al hebben geladen, alleen toepassen (geen fetch).
    if (skipNextPlaySequenceFetchRef.current) {
      skipNextPlaySequenceFetchRef.current = false;
      const actions = playSequenceActionsRef.current;
      if (Array.isArray(actions) && actions.length > 0) {
        applyPlaySequenceActions(actions, null);
        console.log("[WordHitBattle] Applied preloaded playSequence:", actions.length, "actions");
      } else {
        applyPlaySequenceActions([], null);
      }
      // Winner/turn logic hier niet nodig – die loopt via andere effecten
      return;
    }
    
    // Load playSequence from Parse (preferred) or local storage
    async function loadAndApplyPlaySequence() {
      try {
        // Store the current game idNumber to check if it's still valid after async call
        const currentGameIdNumber = game?.idNumber;
        if (!currentGameIdNumber) {
          console.warn("[WordHitBattle] Cannot load playSequence: game.idNumber is missing");
          return;
        }
        
        // First try to get from Parse (most up-to-date)
        const result = await fetchBattlePlaySequenceFromParse(battleInfo.id, currentGameIdNumber);
        
        // Double-check that the game hasn't changed while we were fetching
        // This prevents applying playSequence from an old game to a new game
        if (game?.idNumber !== currentGameIdNumber) {
          console.log("[WordHitBattle] Game changed during playSequence fetch, skipping apply");
          return;
        }
        
        if (result && result.playSequence && Array.isArray(result.playSequence) && result.playSequence.length > 0) {
          // Volledige playSequence voor beurt (vorige game bij lege; hier gewoon object bijwerken)
          try {
            const fullResult = await Parse.Cloud.run("getBattlePlaySequenceObject", { userBattleId: battleInfo.id });
            if (fullResult?.success && fullResult?.playSequence) {
              playSequenceObjectRef.current = fullResult.playSequence;
            }
          } catch (e) {
            console.warn("[WordHitBattle] Failed to set playSequence object:", e);
          }
          setPlaySequenceActions(result.playSequence);
          playSequenceActionsRef.current = result.playSequence;
          lastPlaySequenceLengthRef.current = result.playSequence.length;
          console.log("[WordHitBattle] Applying playSequence to board:", result.playSequence.length, "actions");
          
          // Apply all actions (both players) to restore board state
          applyPlaySequenceActions(result.playSequence, null);
        } else if (result && result.playSequence && Array.isArray(result.playSequence) && result.playSequence.length === 0) {
          // Nieuwe game met lege sequence: volledig object ophalen zodat beurt uit vorige game afgeleid kan worden
          try {
            const fullResult = await Parse.Cloud.run("getBattlePlaySequenceObject", { userBattleId: battleInfo.id });
            if (fullResult?.success && fullResult?.playSequence) {
              playSequenceObjectRef.current = fullResult.playSequence;
            }
          } catch (e) {
            console.warn("[WordHitBattle] Failed to set playSequence object for empty game:", e);
          }
          console.log("[WordHitBattle] New game detected (empty playSequence), clearing board");
          setPlaySequenceActions([]);
          playSequenceActionsRef.current = [];
          lastPlaySequenceLengthRef.current = 0;
          // applyPlaySequenceActions will handle clearing the board when actions array is empty
          applyPlaySequenceActions([], null);
        }
        
        // Turn wordt afgeleid uit playSequenceObject in useEffect (getTurnFromPlaySequenceObject).
        
        // Check if there's a winner (game is finished)
        if (result && result.winner) {
          const remotePlaySequence = result.playSequence || [];
          const remotePlayerScores = result.playerScores || {};
          
          // Use playerScores from cloud (more reliable than recalculating from playSequence)
          // Fallback to calculating from playSequence if playerScores not available
          let currentUserScore = remotePlayerScores[currentUserId] || 0;
          let opponentScore = remotePlayerScores[opponentId] || 0;
          
          // If playerScores not available, calculate from playSequence (fallback)
          if (Object.keys(remotePlayerScores).length === 0) {
            currentUserScore = remotePlaySequence.filter(a => isWordScoredBy(a, currentUserId)).length;
            opponentScore = remotePlaySequence.filter(a => isWordScoredBy(a, opponentId)).length;
          }
          
          const winnerId = result.winner;
          const loserId = winnerId === currentUserId ? opponentId : currentUserId;
          const winnerScore = winnerId === currentUserId ? currentUserScore : opponentScore;
          const loserScore = winnerId === currentUserId ? opponentScore : currentUserScore;
          
          // Get player names (always use "name" field, not username/email)
          const winnerName = winnerId === currentUserId 
            ? (currentUser?.get("name") || "Jij")
            : (battleInfo?.opponent?.name || "Tegenstander");
          
          const loserName = loserId === currentUserId
            ? (currentUser?.get("name") || "Jij")
            : (battleInfo?.opponent?.name || "Tegenstander");
          
          // Set game result for popup
          setGameResult({
            winnerId,
            winnerName,
            loserId,
            loserName,
            winnerScore,
            loserScore,
          });
          
          // Game is finished - check if current user won
          if (result.winner === currentUserId) {
            setHasWon(true);
          } else if (result.winner === opponentId) {
            setHasLost(true);
          }
          
          // Einde van game – winner bekend. De automatische overgang naar de volgende game
          // gebeurt via een aparte effect-hook zodra alle animaties klaar zijn.
        }
      } catch (error) {
        console.warn("[WordHitBattle] Failed to load playSequence from Parse:", error);
      }
    }
    
    loadAndApplyPlaySequence();
  }, [game, battleInfo?.id, game?.idNumber]);

  const resetLocalGameStateForNewGame = useCallback(() => {
    // Hard reset ALL per-game local state so old game data cannot bleed into the next game
    // Do NOT reset actionsThisTurn/turnPlayerId - they are battle-wide; we set them from server when loading next game
    setRevealed(
      Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => false)
      )
    );
    setGuessedWords([]);
    // wrongGuessedWords is restored per battle game key; avoid wiping it after restore (prevents flicker)
    setGuess("");
    setHasWon(false);
    setHasLost(false);
    // actionsThisTurn stays – set from server when we load next game (so "1/2" carries over)
    setGameResult(null);
    setPlaySequenceActions([]);
    playSequenceActionsRef.current = [];
    lastPlaySequenceLengthRef.current = 0;
  }, []);

  const loadBattleGame = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);

      // IMPORTANT: Do NOT trust battleGameId/idNumber from the battle list (can be stale).
      // Always ask server which game THIS user should see (currentGamePerPlayer).
      let effectiveBattleGameId = battleGameId;
      try {
        if (battleInfo?.id) {
          const currentGameResult = await Parse.Cloud.run("getBattleCurrentGame", {
            userBattleId: battleInfo.id,
          });
          if (currentGameResult?.success && currentGameResult?.battleGameId) {
            effectiveBattleGameId = currentGameResult.battleGameId;
            console.log("[WordHitBattle] Using getBattleCurrentGame:", currentGameResult);
          } else {
            console.warn("[WordHitBattle] getBattleCurrentGame returned no battleGameId, falling back to provided battleGameId", currentGameResult);
          }
        }
      } catch (e) {
        console.warn("[WordHitBattle] getBattleCurrentGame failed, falling back to provided battleGameId:", e);
      }

      console.log("[WordHitBattle] Loading battle game:", effectiveBattleGameId);
      const puzzle = await fetchBattleGameFromParse(effectiveBattleGameId, effectiveLangString);
      console.log("[WordHitBattle] Puzzle loaded:", puzzle);
      
      const built = buildGameFromPuzzle(puzzle, effectiveBattleGameId);
      console.log("[WordHitBattle] Game built:", {
        theme: built.theme,
        words: built.words,
        boardRows: built.board.length,
        placements: built.placements.length,
        hasBoard: !!built.board,
        boardNotEmpty: built.board.some(row => row.some(cell => cell !== "")),
      });
      
      setGame(built);

      // Reset game state
      resetLocalGameStateForNewGame();

      // Preload playSequence vóór we de grid tonen, zodat de laatste-beurt-animatie
      // meteen kan starten (geen "pagina laadt en dan gebeurt er even niks").
      if (battleInfo?.id && built?.idNumber != null) {
        try {
          const result = await fetchBattlePlaySequenceFromParse(battleInfo.id, built.idNumber);
          const actions = result?.playSequence && Array.isArray(result.playSequence) ? result.playSequence : [];
          if (actions.length > 0) {
            try {
              const fullResult = await Parse.Cloud.run("getBattlePlaySequenceObject", { userBattleId: battleInfo.id });
              if (fullResult?.success && fullResult?.playSequence) {
                playSequenceObjectRef.current = fullResult.playSequence;
              }
            } catch (e) {
              console.warn("[WordHitBattle] Preload: getBattlePlaySequenceObject failed:", e);
            }
          }
          setPlaySequenceActions(actions);
          playSequenceActionsRef.current = actions;
          lastPlaySequenceLengthRef.current = actions.length;
          skipNextPlaySequenceFetchRef.current = true;

          if (result?.winner) {
            const remotePlaySequence = result.playSequence || [];
            const remotePlayerScores = result.playerScores || {};
            let currentUserScore = remotePlayerScores[currentUserId] || 0;
            let opponentScore = remotePlayerScores[opponentId] || 0;
            if (Object.keys(remotePlayerScores || {}).length === 0) {
              currentUserScore = remotePlaySequence.filter((a) => isWordScoredBy(a, currentUserId)).length;
              opponentScore = remotePlaySequence.filter((a) => isWordScoredBy(a, opponentId)).length;
            }
            const winnerId = result.winner;
            const loserId = winnerId === currentUserId ? opponentId : currentUserId;
            const winnerScore = winnerId === currentUserId ? currentUserScore : opponentScore;
            const loserScore = winnerId === currentUserId ? opponentScore : currentUserScore;
            const winnerName = winnerId === currentUserId ? (currentUser?.get?.("name") || "Jij") : (battleInfo?.opponent?.name || "Tegenstander");
            const loserName = loserId === currentUserId ? (currentUser?.get?.("name") || "Jij") : (battleInfo?.opponent?.name || "Tegenstander");
            setGameResult({ winnerId, winnerName, loserId, loserName, winnerScore, loserScore });
            if (winnerId === currentUserId) setHasWon(true);
            else if (winnerId === opponentId) setHasLost(true);
            if (pulseNextButtonTimeoutRef.current) clearTimeout(pulseNextButtonTimeoutRef.current);
            pulseNextButtonTimeoutRef.current = setTimeout(() => {
              setShouldPulseNextButton(true);
              pulseNextButtonTimeoutRef.current = setTimeout(() => {
                setShouldPulseNextButton(false);
                pulseNextButtonTimeoutRef.current = null;
              }, 3400);
            }, NEXT_BUTTON_PULSE_DELAY_MS);
          }
        } catch (e) {
          console.warn("[WordHitBattle] Preload playSequence failed, useEffect will fetch:", e);
        }
      }
      // Don't set turnPlayerId here - let it be loaded from Parse
      // This ensures we always use the server as source of truth
      // Keep code simple: the dedicated useEffect (depends on game.idNumber) is the single source of truth
      // for loading/applying playSequence. Do not fetch/apply here to avoid double-apply race conditions.
    } catch (error) {
      console.error("[WordHitBattle] Error loading battle game:", error);
      setLoadError(
        error.message ||
          (langString === "nl"
            ? "Kon battle game niet laden."
            : "Could not load battle game.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  function allWordsSolved(nextRevealed = revealed) {
    if (!game) return false;
    return game.words.every((w) =>
      isWordFullyVisible(w, game.placements, nextRevealed)
    );
  }

  function countSolvedWords(nextRevealed = revealed) {
    if (!game) return 0;
    return game.words.reduce((total, word) => {
      return isWordFullyVisible(word, game.placements, nextRevealed)
        ? total + 1
        : total;
    }, 0);
  }

  async function evaluateEndConditions(
    nextRevealed,
    useLocalScores = false,
    actionsSnapshot = null,
    localNewWordsForCurrentUser = 0
  ) {
    if (!game) return;

    const solved = allWordsSolved(nextRevealed);
    const solvedWordCount = countSolvedWords(nextRevealed);

    if (solved && !hasWon && !hasLost) {
      // Alleen winner-save doen als we zelf de actie net deden (actionsSnapshot); niet bij aanroep vanuit poll/load (geen snapshot = ref kan 1 actie missen)
      const haveAuthoritativeSnapshot = actionsSnapshot && actionsSnapshot.length > 0;
      const capturedActions = haveAuthoritativeSnapshot
        ? [...actionsSnapshot]
        : [...playSequenceActionsRef.current];

      // Calculate playerScores
      // If useLocalScores is true (e.g., after a guess/click), use local playSequenceActionsRef
      // which includes the latest action that may not be saved yet
      // Otherwise, try to fetch from cloud first (more reliable for polling/loading)
      let currentUserScore = 0;
      let opponentScore = 0;
      
      if (useLocalScores) {
        currentUserScore = capturedActions.filter(a => isWordScoredBy(a, currentUserId)).length;
        opponentScore = capturedActions.filter(a => isWordScoredBy(a, opponentId)).length;
        console.log("[WordHitBattle] Using local scores (just added action):", { currentUserScore, opponentScore });
      } else if (battleInfo?.id && game?.idNumber) {
        try {
          const result = await fetchBattlePlaySequenceFromParse(battleInfo.id, game.idNumber);
          const playerScores = result?.playerScores || {};
          
          // Use playerScores from cloud if available
          if (Object.keys(playerScores).length > 0) {
            currentUserScore = playerScores[currentUserId] || 0;
            opponentScore = playerScores[opponentId] || 0;
            console.log("[WordHitBattle] Using playerScores from cloud:", { currentUserScore, opponentScore });
          } else {
            currentUserScore = capturedActions.filter(a => isWordScoredBy(a, currentUserId)).length;
            opponentScore = capturedActions.filter(a => isWordScoredBy(a, opponentId)).length;
            console.log("[WordHitBattle] Fallback: calculated scores from playSequence:", { currentUserScore, opponentScore });
          }
        } catch (error) {
          console.error("[WordHitBattle] Failed to fetch playerScores from cloud, using local calculation:", error);
          currentUserScore = capturedActions.filter(a => isWordScoredBy(a, currentUserId)).length;
          opponentScore = capturedActions.filter(a => isWordScoredBy(a, opponentId)).length;
        }
      } else {
        currentUserScore = capturedActions.filter(a => isWordScoredBy(a, currentUserId)).length;
        opponentScore = capturedActions.filter(a => isWordScoredBy(a, opponentId)).length;
      }

      // Update local battle totals immediately so the last word(s) are reflected in the header
      if (
        useLocalScores &&
        haveAuthoritativeSnapshot &&
        localNewWordsForCurrentUser > 0 &&
        currentUserId
      ) {
        setBattleTotalWords(prev => {
          const base =
            (prev && typeof prev === "object" && !Array.isArray(prev))
              ? { ...prev }
              : (battleTotals && typeof battleTotals === "object" && !Array.isArray(battleTotals)
                  ? { ...battleTotals }
                  : {});

          base[currentUserId] = (base[currentUserId] || 0) + localNewWordsForCurrentUser;
          return base;
        });
      }
      
      // Determine winner: player with most words wins
      // If tie, current user wins (first to finish)
      let winnerId = currentUserId;
      let loserId = opponentId;
      let winnerScore = currentUserScore;
      let loserScore = opponentScore;
      
      if (opponentScore > currentUserScore) {
        winnerId = opponentId;
        loserId = currentUserId;
        winnerScore = opponentScore;
        loserScore = currentUserScore;
        setHasLost(true);
      } else {
        setHasWon(true);
      }
      
      // Get player names (always use "name" field, not username/email)
      const winnerName = winnerId === currentUserId 
        ? (currentUser?.get("name") || "Jij")
        : (battleInfo?.opponent?.name || "Tegenstander");
      
      const loserName = loserId === currentUserId
        ? (currentUser?.get("name") || "Jij")
        : (battleInfo?.opponent?.name || "Tegenstander");
      
      // Set game result for popup
      setGameResult({
        winnerId,
        winnerName,
        loserId,
        loserName,
        winnerScore,
        loserScore,
      });
      
      // Einde van game – winner bekend. De automatische overgang naar de volgende game
      // gebeurt via een aparte effect-hook zodra alle animaties klaar zijn.
      
      // BATTLE MODE: IMPORTANT - Do NOT modify User class data
      // hasWon is ONLY a local state variable - it does NOT modify:
      // - User.score
      // - User.numbersPlayed
      // - User.streak
      // - User.maxStreak
      // - User.lastWonId
      // - User.finishedIdNumbers
      // - User.competition data
      // - Any other User class fields
      // Battle Mode is completely separate from daily WordHit scoring
      
      // Save playSequence to Parse when game is won (only saves to UserBattle, not User)
      // IMPORTANT: Alleen opslaan als we de game-eindige actie zélf deden (haveAuthoritativeSnapshot), anders overschrijven we met ref die de laatste guess mist
      // Use capturedActions so poll cannot overwrite ref and drop the last guess
      if (battleInfo?.id && game?.idNumber && capturedActions.length > 0 && haveAuthoritativeSnapshot) {
        if (isSavingGameResultRef.current) return; // voorkom dubbele winner-save
        isSavingGameResultRef.current = true;
        const battleInfoId = battleInfo.id;
        const gameIdNumber = game.idNumber;
        const finalWinnerId = winnerId;

        if (battleSaveDebounceTimeoutRef.current) {
          clearTimeout(battleSaveDebounceTimeoutRef.current);
          battleSaveDebounceTimeoutRef.current = null;
        }
        pendingPushAfterSaveRef.current = false;

        setIsSavingGameResult(true);

        try {
          const finalActions = capturedActions;
          if (!battleInfoId || !gameIdNumber) {
            console.error("[WordHitBattle] Cannot save game result - missing battleInfo.id or game.idNumber:", {
              battleInfoId,
              gameIdNumber,
            });
          } else {
            console.log("[WordHitBattle] Saving game result with winner:", {
              battleInfoId,
              gameIdNumber,
              actionsLength: finalActions.length,
              winnerId: finalWinnerId,
            });

            // Bij game-einde: beurt gaat altijd naar de verliezer, zodat die de volgende game start (2 acties). De winnaar deed net 1 actie (de guess) en mag niet opnieuw 2 acties krijgen.
            const turnForSave = finalWinnerId === currentUserId ? opponentId : currentUserId;

            // Defensieve kopie zodat de array niet elders gemuteerd kan worden vóór/during save
            await requestBattleSave(
              battleInfoId,
              gameIdNumber,
              [...finalActions],
              turnForSave,
              finalWinnerId
            );

            console.log("[WordHitBattle] Game result saved successfully, winner set");
          }
        } catch (error) {
          console.error("[WordHitBattle] Error saving game result:", error);
        } finally {
          isSavingGameResultRef.current = false;
          setIsSavingGameResult(false);
        }
      }
      return;
    }

    // In Battle Mode, there's no loss condition based on clicks
    // Game only ends when all words are found (win)
  }

  function syncGuessedWithVisibility(nextRevealed) {
    if (!game) return;
    const newlySolved = [];
    for (const w of game.words) {
      if (isWordFullyVisible(w, game.placements, nextRevealed)) {
        newlySolved.push(w);
      }
    }
    if (newlySolved.length > 0) {
      setGuessedWords((prev) => Array.from(new Set([...prev, ...newlySolved])));
    }
  }

  // Note: Popup is no longer shown, pulse animation is handled directly when game finishes

  // Handle loading next WordHit
  // In de nieuwe flow wordt dit automatisch aangeroepen na:
  // - einde van alle grid-animaties (replay van laatste beurt), plus 1 seconde extra.
  // - geen lokale animatie (eigen klik/guess) meer actief.
  const handleNextWordHit = async () => {
    console.log("[WordHitBattle] handleNextWordHit called", {
      hasGame: !!game,
      gameIdNumber: game?.idNumber,
      hasBattleInfo: !!battleInfo,
      battleInfoId: battleInfo?.id,
      isSavingGameResult: isSavingGameResultRef.current
    });
    
    // Prevent transition if we're still saving the game result or already transitioning
    if (isSavingGameResultRef.current) {
      console.log("[WordHitBattle] Still saving game result, please wait...");
      return;
    }
    if (isNextTransitioning) {
      console.log("[WordHitBattle] Next transition already in progress");
      return;
    }
    
    if (!game || !battleInfo?.id) {
      console.error("[WordHitBattle] Cannot load next WordHit: missing game or battleInfo");
      return;
    }

    try {
      hasAutoAdvancedRef.current = true;
      if (autoAdvanceFallbackTimeoutRef.current) {
        clearTimeout(autoAdvanceFallbackTimeoutRef.current);
        autoAdvanceFallbackTimeoutRef.current = null;
      }

      setIsLoading(true);
      const battleInfoId = battleInfo.id;
      const stableCurrentGameIdNumber = game?.idNumber ?? null;
      // Keep current grid visible with overlay; don't reset until we have the new game
      console.log("[WordHitBattle] Starting to load next WordHit...");

      const battleOrder = Array.isArray(battleInfo?.battleOrder) ? battleInfo.battleOrder : [];
      if (stableCurrentGameIdNumber === null || stableCurrentGameIdNumber === undefined) {
        console.error("[WordHitBattle] Cannot load next - missing current game idNumber");
        setIsLoading(false);
        return;
      }
      let nextIdNumber = null;
      if (battleOrder.length > 0) {
        const currentIndex = battleOrder.indexOf(stableCurrentGameIdNumber);
        if (currentIndex >= 0 && currentIndex + 1 < battleOrder.length) {
          nextIdNumber = battleOrder[currentIndex + 1];
        }
      }
      if (nextIdNumber === null || nextIdNumber === undefined) {
        console.log("[WordHitBattle] No next game in battleOrder");
        setIsLoading(false);
        return;
      }
      const numericNextIdNumber = Number(nextIdNumber);
      if (isNaN(numericNextIdNumber)) {
        setIsLoading(false);
        return;
      }

      // 1) Show grid immediately: get puzzle from cache (instant) or cloud, then set game so grid is visible (still not playable until playSequence is applied)
      // When prefill=qazx we never use cache: puzzleFromCache is forced to null and we always load from Parse BattleGame class (one cloud call when by idNumber).
      const forceParse = isPrefillQazx();
      const puzzleFromCache = forceParse ? null : getBattleGameByIdNumberFromCache(numericNextIdNumber, effectiveLangString);
      let nextBattleGameId = puzzleFromCache?.battleGameId ?? null;
      let puzzle = puzzleFromCache;
      if (!nextBattleGameId) {
        try {
          const r = await Parse.Cloud.run("getBattleGame", { idNumber: numericNextIdNumber });
          nextBattleGameId = r?.battleGameId || null;
          // When prefill=qazx use this single Parse response as puzzle (no cache, no second call)
          if (forceParse && r?.success && nextBattleGameId) {
            const theme = effectiveLangString === "nl" ? (r.themeNL ?? r.theme) : (r.theme ?? r.themeNL);
            const words = effectiveLangString === "nl" ? (r.wordsNL ?? r.words) : (r.words ?? r.wordsNL);
            puzzle = { theme, words, idNumber: numericNextIdNumber, battleGameId: nextBattleGameId };
          }
        } catch {
          /* ignore */
        }
      }
      if (!nextBattleGameId) {
        console.error("[WordHitBattle] Failed to get BattleGame id for idNumber:", numericNextIdNumber);
        setIsLoading(false);
        return;
      }
      if (!puzzle) {
        puzzle = await fetchBattleGameFromParse(nextBattleGameId, effectiveLangString);
      }
      const built = buildGameFromPuzzle(puzzle, nextBattleGameId);

      // Eerst playSequence ophalen en (bij nieuwe game) refs zetten vóór we game wisselen,
      // anders wint loadAndApplyPlaySequence de race en wordt 0/2 getoond i.p.v. 1/2
      let playSequence = {};
      try {
        const result = await Parse.Cloud.run("getBattlePlaySequenceObject", { userBattleId: battleInfoId });
        if (result?.success && result.playSequence) playSequence = result.playSequence;
      } catch (error) {
        console.warn("[WordHitBattle] getBattlePlaySequenceObject failed:", error);
      }

      const existingGameData = playSequence[String(numericNextIdNumber)];

      // Flip start: pas tijdens de "edge" (50%) wisselen we de game om jumps te verbergen.
      setIsNextTransitioning(true);
      await new Promise((resolve) => setTimeout(resolve, Math.floor(GRID_FLIP_DURATION_MS / 2)));

      if (existingGameData && typeof existingGameData === "object") {
        resetLocalGameStateForNewGame();
        setGame(built);
        setTurnPlayerId(null);
        turnPlayerIdRef.current = null;

        playSequenceObjectRef.current = playSequence;
        const remotePlaySequence = Array.isArray(existingGameData.actions) ? existingGameData.actions : [];
        setPlaySequenceActions(remotePlaySequence);
        playSequenceActionsRef.current = remotePlaySequence;
        lastPlaySequenceLengthRef.current = remotePlaySequence.length;
        setTimeout(() => {
          applyPlaySequenceActions(remotePlaySequence, null);
        }, 0);

        try {
          await Parse.Cloud.run("updateCurrentGamePerPlayer", {
            userBattleId: battleInfo.id,
            idNumber: numericNextIdNumber,
          });
        } catch (e) {
          console.warn("[WordHitBattle] updateCurrentGamePerPlayer failed:", e);
        }

        console.log("[WordHitBattle] Loaded next game from battleOrder:", numericNextIdNumber);

        await new Promise((resolve) => setTimeout(resolve, Math.ceil(GRID_FLIP_DURATION_MS / 2)));
        setIsLoading(false);
        return;
      }

      // Nieuwe game: volledig playSequence-object bijwerken (vorige game staat erin; nieuwe game met lege acties)
      playSequenceObjectRef.current = {
        ...playSequence,
        [String(numericNextIdNumber)]: { idNumber: numericNextIdNumber, actions: [] },
      };

      resetLocalGameStateForNewGame();
      setGame(built);
      setTurnPlayerId(null);
      turnPlayerIdRef.current = null;
      setPlaySequenceActions([]);
      playSequenceActionsRef.current = [];
      lastPlaySequenceLengthRef.current = 0;
      setTimeout(() => {
        applyPlaySequenceActions([], null);
      }, 0);

      try {
        const prevGameData = playSequence[String(stableCurrentGameIdNumber)];
        const prevActions = prevGameData && Array.isArray(prevGameData.actions) ? prevGameData.actions : [];
        const nextGameStarterId = getTurnFromPlaySequence([], prevActions, currentUserId, opponentId).turnPlayerId;
        await requestBattleSave(
          battleInfo.id,
          numericNextIdNumber,
          [],
          nextGameStarterId,
          null
        );
        await Parse.Cloud.run("updateCurrentGamePerPlayer", {
          userBattleId: battleInfo.id,
          idNumber: numericNextIdNumber,
        });
        console.log("[WordHitBattle] New game in battleOrder created:", numericNextIdNumber, "turnPlayer (from sequence):", nextGameStarterId);
      } catch (saveError) {
        console.error("[WordHitBattle] Error saving new game:", saveError);
      }

      await new Promise((resolve) => setTimeout(resolve, Math.ceil(GRID_FLIP_DURATION_MS / 2)));
      setIsLoading(false);
    } catch (error) {
      console.error("[WordHitBattle] Error loading next WordHit:", error);
      console.error("[WordHitBattle] Error details:", {
        message: error?.message,
        code: error?.code,
        stack: error?.stack,
        game: game?.idNumber,
        battleInfo: battleInfo?.id
      });
      setIsLoading(false);
    } finally {
      setIsNextTransitioning(false);
    }
  };

  /** Debounced save: wacht even zodat twee snelle klikken/guesses in één save gaan. */
  function scheduleDebouncedBattleSave(shouldSendPushIfTurnToOpponent) {
    if (!battleInfo?.id || game?.idNumber == null) return;
    if (battleSaveDebounceTimeoutRef.current) clearTimeout(battleSaveDebounceTimeoutRef.current);
    if (shouldSendPushIfTurnToOpponent) pendingPushAfterSaveRef.current = true;

    const capturedSendPush = !!shouldSendPushIfTurnToOpponent;
    const capturedOpponentId = opponentId;
    const capturedBattleId = battleInfo?.id;

    battleSaveDebounceTimeoutRef.current = setTimeout(async () => {
      battleSaveDebounceTimeoutRef.current = null;
      const userBattleId = battleInfo?.id;
      const idNumber = game?.idNumber;
      const actions = playSequenceActionsRef.current;
      const turnForSave = turnPlayerIdRef.current;
      if (!userBattleId || idNumber == null || !actions?.length) return;

      console.log("[WordHitBattle] Saving after action (debounced), actions:", actions.length);
      const saveSuccess = await requestBattleSave(userBattleId, idNumber, actions, turnForSave);
      console.log("[WordHitBattle] Debounced save result:", { saveSuccess, capturedSendPush, capturedOpponentId: !!capturedOpponentId, capturedBattleId: !!capturedBattleId });
      if (saveSuccess && capturedSendPush && capturedOpponentId && capturedBattleId) {
        if (!(lastTurnPushSentRef.current?.battleId === capturedBattleId && lastTurnPushSentRef.current?.turnPlayerId === capturedOpponentId)) {
          lastTurnPushSentRef.current = { battleId: capturedBattleId, turnPlayerId: capturedOpponentId };
          Parse.Cloud.run("sendBattleTurnNotification", { opponentId: capturedOpponentId, battleId: capturedBattleId }).catch((err) => {
            console.warn("[WordHitBattle] sendBattleTurnNotification failed:", err);
          });
        }
        pendingPushAfterSaveRef.current = false;
      }
    }, BATTLE_SAVE_DEBOUNCE_MS);
  }

  /** Serialize battle saves so we never overwrite with an older snapshot (only one save in flight; pending flush after) */
  async function requestBattleSave(userBattleId, idNumber, actions, newTurnPlayerId = null, winner = null) {
    if (battleSaveInFlightRef.current) {
      battleSavePendingRef.current = true;
      if (newTurnPlayerId !== null && newTurnPlayerId !== undefined) {
        battleSavePendingTurnRef.current = newTurnPlayerId;
        // Apply turn to UI immediately so user sees "opponent's turn" (setTimeout so we're not inside a lifecycle; runs after any in-flight fetch)
        isSavingTurnRef.current = true;
        turnPlayerIdRef.current = newTurnPlayerId;
        actionsThisTurnRef.current = 0;
        setTimeout(() => {
          flushSync(() => {
            setTurnPlayerId(newTurnPlayerId);
            setActionsThisTurn(0);
          });
        }, 0);
      }
      if (winner !== null && winner !== undefined) {
        battleSavePendingWinnerRef.current = winner;
      }
      // Bewaar acties zodat pending save niet playSequenceActionsRef gebruikt (die kan door poll overschreven zijn)
      if (actions != null && Array.isArray(actions) && actions.length > 0) {
        battleSavePendingActionsRef.current = actions;
      }
      return;
    }
    battleSaveInFlightRef.current = true;
    try {
      const success = await saveBattlePlaySequenceToParse(userBattleId, idNumber, actions, newTurnPlayerId, winner);
      return success;
    } finally {
      battleSaveInFlightRef.current = false;
      if (battleSavePendingRef.current) {
        battleSavePendingRef.current = false;
        const pendingTurn = battleSavePendingTurnRef.current;
        const pendingWinner = battleSavePendingWinnerRef.current;
        const pendingActions = battleSavePendingActionsRef.current;
        battleSavePendingTurnRef.current = null;
        battleSavePendingWinnerRef.current = null;
        battleSavePendingActionsRef.current = null;
        const latest = pendingActions && pendingActions.length > 0 ? pendingActions : playSequenceActionsRef.current;
        if (latest.length > 0 && battleInfo?.id && game?.idNumber != null) {
          const saveSuccess = await requestBattleSave(
            battleInfo.id,
            game.idNumber,
            latest,
            pendingTurn !== null && pendingTurn !== undefined ? pendingTurn : null,
            pendingWinner !== null && pendingWinner !== undefined ? pendingWinner : null
          );
        }
      }
    }
  }

  async function saveBattlePlaySequenceToParse(userBattleId, idNumber, actions, newTurnPlayerId = null, winner = null) {
    // Validate parameters - idNumber can be 0, so check for null/undefined specifically
    if (!userBattleId || idNumber === null || idNumber === undefined || !Array.isArray(actions)) {
      console.error("[WordHitBattle] saveBattlePlaySequenceToParse called with invalid parameters:", {
        userBattleId,
        idNumber,
        actionsIsArray: Array.isArray(actions),
        actionsLength: actions?.length
      });
      if (newTurnPlayerId != null) isSavingTurnRef.current = false;
      return false;
    }
    
    // Mark that we're saving a turn change
    if (newTurnPlayerId !== null && newTurnPlayerId !== undefined) {
      isSavingTurnRef.current = true;
    }
    
    try {
      // Ensure idNumber is a valid number
      const numericIdNumber = typeof idNumber === "number" ? idNumber : Number(idNumber);
      if (isNaN(numericIdNumber)) {
        console.error("[WordHitBattle] saveBattlePlaySequenceToParse: idNumber is not a valid number:", idNumber);
        if (newTurnPlayerId != null) isSavingTurnRef.current = false;
        return false;
      }
      
      // When saving with a winner (game finished), always use cloud so server can set battleWinnerId and send "battle afgelopen" push
      const isGameEndWithWinner = winner != null && winner !== undefined;
      
      // Try direct save first (faster, no cloud function overhead)
      // Only works if user has ACL permissions on UserBattle. Skip when game ended with winner so server sends end-battle push.
      if (!isGameEndWithWinner) try {
        const UserBattle = Parse.Object.extend("UserBattle");
        const query = new Parse.Query(UserBattle);
        query.equalTo("objectId", userBattleId);
        const userBattle = await query.first();
        
        if (userBattle) {
          // Get existing playSequence
          const existingPlaySequence = userBattle.get("playSequence") || {};
          const playSequenceObject = typeof existingPlaySequence === 'object' && !Array.isArray(existingPlaySequence)
            ? JSON.parse(JSON.stringify(existingPlaySequence)) // Deep copy
            : {};
          
          // Bij winner altijd onze acties; anders niet overschrijven met kortere lijst (laatste guess mag niet verdwijnen)
          const existingEntry = playSequenceObject[String(numericIdNumber)];
          const existingActions = existingEntry && Array.isArray(existingEntry.actions) ? existingEntry.actions : [];
          const actionsToWrite = (winner != null && winner !== undefined) || actions.length >= existingActions.length
            ? actions
            : existingActions;
          const winnerToWrite = (winner != null && winner !== undefined) || actions.length >= existingActions.length
            ? (winner || null)
            : (existingEntry?.winner ?? null);

          playSequenceObject[String(numericIdNumber)] = {
            idNumber: numericIdNumber,
            actions: actionsToWrite,
            winner: winnerToWrite
          };
          
          userBattle.set("playSequence", playSequenceObject);
          
          // Update turnPlayer if provided (createWithoutData makes a valid Pointer for save)
          if (newTurnPlayerId !== null && newTurnPlayerId !== undefined) {
            const turnPlayerPointer = Parse.User.createWithoutData(String(newTurnPlayerId));
            userBattle.set("turnPlayer", turnPlayerPointer);
          }
          
          // Calculate and update playerScores for current game (idNumber)
          // A guess scores 1, or 2 when it completes two or more board words (same cap as daily).
          // A click scores +1 per word_solved.
          const currentGamePlayerScores = {};
          for (const action of actionsToWrite) {
            const p = getActionPayload(action);
            const playerId = getActionPlayerId(action);
            if (!playerId) continue;
            if (p && p.type === "guess" && p.correct === true) {
              currentGamePlayerScores[playerId] = (currentGamePlayerScores[playerId] || 0) + 1;
            } else if (p && p.type === "word_solved") {
              currentGamePlayerScores[playerId] = (currentGamePlayerScores[playerId] || 0) + 1;
            }
          }
          
          // Get existing playerScores
          const existingPlayerScores = userBattle.get("playerScores") || {};
          let playerScoresObject = {};
          if (existingPlayerScores && typeof existingPlayerScores === 'object' && !Array.isArray(existingPlayerScores)) {
            const hasNumericKeys = Object.keys(existingPlayerScores).some(key => /^\d+$/.test(key));
            if (hasNumericKeys) {
              // Already structured per game - keep existing scores for other games
              playerScoresObject = { ...existingPlayerScores };
            } else {
              // Flat structure (backwards compatibility) - convert to per-game structure
              playerScoresObject["1"] = existingPlayerScores;
            }
          }
          
          // Update playerScores for current game (idNumber)
          playerScoresObject[String(numericIdNumber)] = currentGamePlayerScores;
          userBattle.set("playerScores", playerScoresObject);
          
          // Calculate and update totalDiscoveredWords (sum across all games)
          const totalDiscoveredWords = {};
          if (playerScoresObject && typeof playerScoresObject === 'object' && !Array.isArray(playerScoresObject)) {
            const hasNumericKeys = Object.keys(playerScoresObject).some(key => /^\d+$/.test(key));
            if (hasNumericKeys) {
              // Structured per game - sum across all games
              for (const [idNumberKey, gameScores] of Object.entries(playerScoresObject)) {
                if (gameScores && typeof gameScores === 'object' && !Array.isArray(gameScores)) {
                  for (const [playerId, wordCount] of Object.entries(gameScores)) {
                    totalDiscoveredWords[playerId] = (totalDiscoveredWords[playerId] || 0) + (wordCount || 0);
                  }
                }
              }
            } else {
              // Flat structure (backwards compatibility) - use as is
              for (const [playerId, wordCount] of Object.entries(playerScoresObject)) {
                totalDiscoveredWords[playerId] = wordCount || 0;
              }
            }
          }
          
          // Store totalDiscoveredWords in UserBattle
          userBattle.set("totalDiscoveredWords", totalDiscoveredWords);
          
          // Try to save directly (will fail if no ACL permissions)
          await userBattle.save();
          
          console.log("[WordHitBattle] Play sequence saved directly to Parse (no cloud function needed)", 
            newTurnPlayerId !== null ? `(turnPlayerId: ${newTurnPlayerId})` : "", 
            winner ? `(winner: ${winner})` : "");
          
          if (newTurnPlayerId != null) lastTurnSaveCompletedAtRef.current = Date.now();
          lastPlaySequenceLengthRef.current = actions.length;
          
          // Refresh battle totals after successful save
          if (refreshBattleTotalsRef.current) {
            await refreshBattleTotalsRef.current();
          }
          
          // Clear the saving flag
          if (newTurnPlayerId !== null && newTurnPlayerId !== undefined) {
            setTimeout(() => {
              isSavingTurnRef.current = false;
              console.log("[WordHitBattle] Turn save flag cleared, polling can resume");
            }, 2000);
          }
          
          return true;
        }
      } catch (directSaveError) {
        console.error("[WordHitBattle] Direct save failed:", directSaveError?.message || directSaveError);
        isSavingTurnRef.current = false;
        // Fall through to cloud fallback below (don't return false yet)
      }
      // Cloud fallback: when direct save failed or userBattle was null, save via cloud so we get a definite success/false and can send push
      try {
        const cloudResult = await Parse.Cloud.run("saveBattlePlaySequence", {
          userBattleId: String(userBattleId),
          idNumber: numericIdNumber,
          actions,
          turnPlayerId: newTurnPlayerId ?? undefined,
          winner: winner ?? undefined,
        });
        if (cloudResult && cloudResult.success) {
          console.log("[WordHitBattle] Play sequence saved via cloud function", newTurnPlayerId !== null ? `(turnPlayerId: ${newTurnPlayerId})` : "", winner ? `(winner: ${winner})` : "");
          if (newTurnPlayerId != null) lastTurnSaveCompletedAtRef.current = Date.now();
          lastPlaySequenceLengthRef.current = actions.length;
          if (newTurnPlayerId !== null && newTurnPlayerId !== undefined) {
            setTimeout(() => { isSavingTurnRef.current = false; }, 2000);
          }
          return true;
        }
      } catch (cloudError) {
        console.error("[WordHitBattle] Cloud saveBattlePlaySequence failed:", cloudError?.message || cloudError);
      }
      isSavingTurnRef.current = false;
      return false;
    } catch (error) {
      console.error("[WordHitBattle] Failed to save play sequence to Parse:", error);
      isSavingTurnRef.current = false;
      return false;
    }
  }

  async function fetchBattlePlaySequenceFromParse(userBattleId, idNumber) {
    // Validate parameters - check for null/undefined specifically
    const numericIdNumber = typeof idNumber === "number" ? idNumber : Number(idNumber);
    if (!userBattleId || idNumber === null || idNumber === undefined || isNaN(numericIdNumber)) {
      const stack = new Error().stack;
      console.warn("[WordHitBattle] fetchBattlePlaySequenceFromParse called with invalid parameters:", {
        userBattleId,
        idNumber,
        idNumberType: typeof idNumber,
        numericIdNumber,
        stack,
      });
      return null;
    }
    
    try {
      const result = await Parse.Cloud.run("getBattlePlaySequence", {
        userBattleId: String(userBattleId),
        idNumber: numericIdNumber, // Ensure it's a valid number
      });
        if (result && result.success) {
        // Turn niet uit server gebruiken – altijd uit playSequence afleiden
        const playSequenceArray = getPlaySequenceArray(result.playSequence, numericIdNumber);
        return {
          playSequence: Array.isArray(result.playSequence) ? result.playSequence : playSequenceArray,
          turnPlayerId: null,
          winner: result.winner || null,
          playerScores: result.playerScores || {},
          actionsThisTurn: result.actionsThisTurn,
        };
      }
    } catch (error) {
      console.error("[WordHitBattle] Failed to fetch play sequence from Parse:", error);
    }
    return { playSequence: [], turnPlayerId: null, winner: null, playerScores: {} };
  }

  function applyPlaySequenceActions(actions, targetPlayer = null) {
    // Apply actions to the board
    // If targetPlayer is null, apply all actions (for initial load or next-game load)
    // If targetPlayer is specified, only apply actions from that player (e.g. polling)
    const activeGame = gameRef.current;
    if (!activeGame || !Array.isArray(actions)) {
      console.warn("[WordHitBattle] Cannot apply playSequence: game not loaded or invalid actions");
      return;
    }

    const emptyRevealed = () =>
      Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => false)
      );

    // Full load (targetPlayer === null): always start from empty so we never show the previous game's revealed cells.
    // Incremental (targetPlayer !== null): use current state as base (same game, adding opponent actions).
    let newRevealed;
    let newGuessedWords;
    if (targetPlayer === null) {
      newRevealed = emptyRevealed();
      newGuessedWords = [];
    } else if (actions.length === 0) {
      newRevealed = emptyRevealed();
      newGuessedWords = [];
    } else {
      newRevealed = revealed.map((row) => row.slice());
      newGuessedWords = [...guessedWords];
    }

    for (const action of actions) {
      if (targetPlayer !== null) {
        const actionPlayerId = getActionPlayerId(action);
        const matches = actionPlayerId === targetPlayer || (typeof targetPlayer === "number" && action.player === targetPlayer);
        if (!matches) continue;
      }

      const payload = getActionPayload(action);
      if (payload.type === "click") {
        const { r, c } = payload;
        if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
          const letter = activeGame.board[r][c];
          if (letter && letter !== "") {
            newRevealed = revealAllOfLetter(letter, newRevealed, activeGame.board);
          } else {
            newRevealed[r][c] = true;
          }
        }
      } else if (payload.type === "guess" && payload.revealsLetters) {
        const { word } = payload;
        if (word) {
          for (let i = 0; i < word.length; i++) {
            newRevealed = revealAllOfLetter(word[i], newRevealed, activeGame.board);
          }
        }
      } else if (payload.type === "guess" && payload.correct) {
        const { word } = payload;
        if (word && !newGuessedWords.includes(word)) {
          newGuessedWords.push(word);
          newRevealed = revealWord(word, activeGame.placements, newRevealed);
          for (let i = 0; i < word.length; i++) {
            const letter = word[i];
            newRevealed = revealAllOfLetter(letter, newRevealed, activeGame.board);
          }
        }
      } else if (payload.type === "word_solved" && payload.word) {
        const { word } = payload;
        newRevealed = revealWord(word, activeGame.placements, newRevealed);
        for (let i = 0; i < word.length; i++) {
          newRevealed = revealAllOfLetter(word[i], newRevealed, activeGame.board);
        }
      }
    }

    setRevealed(newRevealed);
    setGuessedWords(newGuessedWords);
    syncGuessedWithVisibility(newRevealed);
    // Use local scores since we just added a click action (not saved yet)
    void evaluateEndConditions(newRevealed, true, null, 0);
  }

  function clickCell(r, c) {
    if (isOffline) return;
    if (hasWon || hasLost) return;
    if (isLoading) return;
    if (isLocalAnimating) return;
    const { currentActions, previousActionsWhenEmpty } = getCurrentAndPreviousActions();
    const derived = getTurnFromPlaySequence(currentActions, previousActionsWhenEmpty, currentUserId, opponentId);
    if (!derived || derived.turnPlayerId !== currentUserId || derived.actionsThisTurn >= MAX_ACTIONS_PER_TURN) return;

    // Zodra de huidige speler een eigen actie doet, stop de replay/animatie niet meer tonen
    setFlashRevealed(false);

    const already = revealed[r][c];
    if (already) return;

    const letter = game.board[r][c];

    const clickTimestamp = Date.now();
    const newAction = {
      user: currentUserId,
      idNumber: game.idNumber,
      action: { type: "click", r, c, timestamp: clickTimestamp },
    };

    let newRev = revealed.map((row) => row.slice());
    const beforeClick = revealed.map((row) => row.slice());

    if (letter && letter !== "") {
      newRev = revealAllOfLetter(letter, newRev, game.board);
    } else {
      newRev[r][c] = true;
    }

    // Bepaal cells die door deze klik voor het eerst zichtbaar zijn geworden en dezelfde letter hebben
    if (letter && letter !== "") {
      const delays = Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => null)
      );
      let idx = 0;
      const BASE_LOCAL_DELAY_MS = 1100;

      // 1) Eerst de daadwerkelijk aangeklikte cel (r, c), als die net nieuw zichtbaar is geworden.
      const clickedIsNewlyRevealed =
        newRev[r][c] && !beforeClick[r][c] && game.board[r][c] === letter;
      if (clickedIsNewlyRevealed) {
        delays[r][c] = 0;
        idx = 1;
      }

      // 2) Verzamel andere nieuwe instanties van dezelfde letter.
      const otherCells = [];
      for (let rr = 0; rr < GRID_SIZE; rr++) {
        for (let cc = 0; cc < GRID_SIZE; cc++) {
          if (rr === r && cc === c) continue;
          const isNewlyRevealed = newRev[rr][cc] && !beforeClick[rr][cc];
          if (isNewlyRevealed && game.board[rr][cc] === letter) {
            otherCells.push({ rr, cc });
          }
        }
      }
      // Woorden die nu volledig zichtbaar zijn (na deze klik)
      const placements = game.placements || [];
      const wordsNowVisible = (game.words || []).filter((w) =>
        isWordFullyVisible(w, placements, newRev)
      );
      const cellsInRevealedWords = new Set();
      for (const w of wordsNowVisible) {
        const cells = getWordCells(w, placements);
        for (const { r: cr, c: cc } of cells) {
          cellsInRevealedWords.add(`${cr},${cc}`);
        }
      }
      // Eerst: cellen die onderdeel zijn van een nu zichtbaar woord (scanvolgorde)
      const partOfRevealedWord = otherCells
        .filter(({ rr, cc }) => cellsInRevealedWords.has(`${rr},${cc}`))
        .sort((a, b) => (a.rr !== b.rr ? a.rr - b.rr : a.cc - b.cc));
      // Daarna: de rest (scanvolgorde)
      const partOfRevealedSet = new Set(partOfRevealedWord.map(({ rr, cc }) => `${rr},${cc}`));
      const rest = otherCells
        .filter(({ rr, cc }) => !partOfRevealedSet.has(`${rr},${cc}`))
        .sort((a, b) => (a.rr !== b.rr ? a.rr - b.rr : a.cc - b.cc));
      for (const { rr, cc } of [...partOfRevealedWord, ...rest]) {
        delays[rr][cc] = idx * BASE_LOCAL_DELAY_MS;
        idx += 1;
      }
      setLocalRevealDelays(idx > 0 ? delays : null);

      if (idx > 0) {
        const FLIP_DURATION_MS = 1100;
        const totalMs = (idx - 1) * BASE_LOCAL_DELAY_MS + FLIP_DURATION_MS;
        startLocalAnimation(totalMs);
      } else {
        startLocalAnimation(0);
      }
    } else {
      /* Miss: één cel met delay 0 zodat die twee keer pulseert */
      const missDelays = Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => null)
      );
      missDelays[r][c] = 0;
      setLocalRevealDelays(missDelays);

      // Miss-animatie mag de grid niet blokkeren
      startLocalAnimation(0);
    }

    const newlySolvedWords = game.words.filter(
      (w) => isWordFullyVisible(w, game.placements, newRev) && !isWordFullyVisible(w, game.placements, revealed)
    );
    const actionsToAdd = [
      newAction,
      ...newlySolvedWords.map((word) => ({
        user: currentUserId,
        idNumber: game.idNumber,
        action: { type: "word_solved", word },
      })),
    ];

    const wasOurTurnBefore = derived.turnPlayerId === currentUserId;
    const updatedActions = [...playSequenceActionsRef.current, ...actionsToAdd];
    playSequenceObjectRef.current = {
      ...playSequenceObjectRef.current,
      [String(game.idNumber)]: { idNumber: game.idNumber, actions: updatedActions },
    };
    setPlaySequenceActions(updatedActions);
    playSequenceActionsRef.current = updatedActions;

    setRevealed(newRev);
    syncGuessedWithVisibility(newRev);
    void evaluateEndConditions(newRev, true, updatedActions, newlySolvedWords.length);

    // Bij game-einde doet evaluateEndConditions één save (acties + winner); geen tweede save plannen
    if (!allWordsSolved(newRev)) {
      const shouldSendPush = wasOurTurnBefore && derived.actionsThisTurn === 1;
      if (shouldSendPush) turnPlayerIdRef.current = opponentId;
      scheduleDebouncedBattleSave(shouldSendPush);
    }
  }

  function showUnknownWordFeedback() {
    if (guessFeedbackTimerRef.current) {
      window.clearTimeout(guessFeedbackTimerRef.current);
      guessFeedbackTimerRef.current = null;
    }
    setGuessFeedback(effectiveLangString === "nl" ? "Onbekend woord" : "Unknown word");
    setGuessShake(true);
    window.setTimeout(() => setGuessShake(false), 400);
    guessFeedbackTimerRef.current = window.setTimeout(() => {
      setGuessFeedback("");
      guessFeedbackTimerRef.current = null;
    }, 2000);
  }

  function showMaxLengthFeedback() {
    if (guessFeedbackTimerRef.current) {
      window.clearTimeout(guessFeedbackTimerRef.current);
      guessFeedbackTimerRef.current = null;
    }
    setGuessFeedback(effectiveLangString === "nl" ? "Max. 7 letters" : "Max 7 letters");
    setGuessShake(true);
    window.setTimeout(() => setGuessShake(false), 400);
    guessFeedbackTimerRef.current = window.setTimeout(() => {
      setGuessFeedback("");
      guessFeedbackTimerRef.current = null;
    }, 2000);
  }

  function handleGuessChange(e) {
    const next = e.target.value.toUpperCase();
    if (next.length > MAX_GUESS_WORD_LENGTH) {
      showMaxLengthFeedback();
      setGuess(next.slice(0, MAX_GUESS_WORD_LENGTH));
      return;
    }
    setGuess(next);
  }

  async function submitGuess(e) {
    e.preventDefault();
    e.stopPropagation();
    if (isOffline) return;
    if (hasWon || hasLost) return;
    if (isLocalAnimating) return;
    const { currentActions, previousActionsWhenEmpty } = getCurrentAndPreviousActions();
    const derived = getTurnFromPlaySequence(currentActions, previousActionsWhenEmpty, currentUserId, opponentId);
    if (!derived || derived.turnPlayerId !== currentUserId || derived.actionsThisTurn >= MAX_ACTIONS_PER_TURN) return;
    // Ook bij een guess: zodra de huidige speler een actie doet, laatste-tegenstander-animatie stoppen.
    setFlashRevealed(false);
    if (!guess.trim()) return;

    const trimmedGuess = guess.trim();
    const guessUp = trimmedGuess.toUpperCase();

    if (guessedWords.includes(guessUp) || wrongGuessedWords.includes(guessUp)) {
      setGuess("");
      return;
    }

    const dictionaryGuessEnabled = isDictionaryGuessEnabled(game.idNumber, { battle: true });
    const isOnGrid = game.words.includes(guessUp);

    if (dictionaryGuessEnabled && !isOnGrid) {
      let dictionaryReady = isDictionaryReady(effectiveLangString);
      try {
        await ensureDictionaryLoaded(effectiveLangString);
        dictionaryReady = isDictionaryReady(effectiveLangString);
      } catch (error) {
        console.warn("Failed to load word dictionary", error);
      }
      if (dictionaryReady && !isValidWord(guessUp, effectiveLangString)) {
        showUnknownWordFeedback();
        return;
      }
    }
    const revealsLetters =
      dictionaryGuessEnabled &&
      isDictionaryReady(effectiveLangString) &&
      !isOnGrid &&
      isValidWord(guessUp, effectiveLangString);

    const guessTimestamp = Date.now();
    const newAction = {
      user: currentUserId,
      idNumber: game.idNumber,
      action: {
        type: "guess",
        word: guessUp,
        timestamp: guessTimestamp,
        correct: isOnGrid,
        revealsLetters,
      },
    };

    const updatedWithGuess = [...playSequenceActionsRef.current, newAction];

    const wasOurTurnBefore = derived.turnPlayerId === currentUserId;

    let newRev = revealed.map((row) => row.slice());
    const beforeGuess = revealed.map((row) => row.slice());
    const wordsVisibleBefore = game.words.filter((word) =>
      isWordFullyVisible(word, game.placements, revealed)
    );
    let newlySolvedFromWrongGuess = [];

    if (!isOnGrid) {
      setWrongGuessedWords((prev) => {
        if (prev.includes(guessUp)) return prev;
        const next = [...prev, guessUp];
        const key = battleWrongKeyRef.current;
        if (key) storeBattleWrongWords(key, next);
        return next;
      });

      let wrongOverlayDelayMs = 0;
      if (revealsLetters) {
        for (let i = 0; i < guessUp.length; i++) {
          const letter = guessUp[i];
          newRev = revealAllOfLetter(letter, newRev, game.board);
        }

        const delays = Array.from({ length: GRID_SIZE }, () =>
          Array.from({ length: GRID_SIZE }, () => null)
        );
        let idx = 0;
        const BASE_LOCAL_DELAY_MS = 1100;
        for (let rr = 0; rr < GRID_SIZE; rr++) {
          for (let cc = 0; cc < GRID_SIZE; cc++) {
            if (newRev[rr][cc] && !beforeGuess[rr][cc]) {
              delays[rr][cc] = idx * BASE_LOCAL_DELAY_MS;
              idx += 1;
            }
          }
        }
        const FLIP_DURATION_MS = 1100;
        const totalMs = idx > 0 ? (idx - 1) * BASE_LOCAL_DELAY_MS + FLIP_DURATION_MS : 0;
        wrongOverlayDelayMs = totalMs;
        if (idx > 0) {
          setLocalRevealDelays(delays);
          startLocalAnimation(totalMs);
          if (pendingSolvedWordTimersRef.current[guessUp]) {
            window.clearTimeout(pendingSolvedWordTimersRef.current[guessUp]);
          }
          pendingSolvedWordTimersRef.current[guessUp] = window.setTimeout(() => {
            setLocalRevealDelays(null);
            pendingSolvedWordTimersRef.current[guessUp] = null;
          }, totalMs);
        }
      }

      newlySolvedFromWrongGuess = game.words.filter(
        (w) => isWordFullyVisible(w, game.placements, newRev) && !wordsVisibleBefore.includes(w)
      );

      const overlayDelayMs = wrongOverlayDelayMs;
      const OVERLAY_DURATION_MS = 1800;
      if (newlySolvedFromWrongGuess.length > 0) {
        setTimeout(() => {
          setShowCheckOverlay(true);
          setTimeout(() => {
            setShowCheckOverlay(false);
          }, OVERLAY_DURATION_MS);
        }, overlayDelayMs);
      } else {
        setTimeout(() => {
          setShowCrossOverlay(true);
          setTimeout(() => {
            setShowCrossOverlay(false);
          }, OVERLAY_DURATION_MS);
        }, overlayDelayMs);
      }

    } else {
      setGuessedWords((prev) => [...prev, guessUp]);
      newRev = revealWord(guessUp, game.placements, newRev);

      for (let i = 0; i < guessUp.length; i++) {
        const letter = guessUp[i];
        newRev = revealAllOfLetter(letter, newRev, game.board);
      }

      // Per-letter flip bij correcte guess: eerst letters van het geraden woord, daarna overige nieuw zichtbare letters.
      if (game?.placements && Array.isArray(game.words)) {
        const delays = Array.from({ length: GRID_SIZE }, () =>
          Array.from({ length: GRID_SIZE }, () => null)
        );
        let idx = 0;
        const BASE_LOCAL_DELAY_MS = 1100;
        const placements = game.placements || [];
        const wordCells = getWordCells(guessUp, placements);
        const wordCellsSet = new Set(wordCells.map(({ r, c }) => `${r},${c}`));
        // 1) Eerst: letters van het geraden woord (woordvolgorde)
        for (const { r, c } of wordCells) {
          const isNewlyRevealed = newRev[r][c] && !beforeGuess[r][c];
          if (isNewlyRevealed) {
            delays[r][c] = idx * BASE_LOCAL_DELAY_MS;
            idx += 1;
          }
        }
        // 2) Overige nieuw zichtbare cellen: eerst deel van een revealed woord, daarna rest (scan)
        const otherCells = [];
        for (let rr = 0; rr < GRID_SIZE; rr++) {
          for (let cc = 0; cc < GRID_SIZE; cc++) {
            if (wordCellsSet.has(`${rr},${cc}`)) continue;
            const isNewlyRevealed = newRev[rr][cc] && !beforeGuess[rr][cc];
            if (isNewlyRevealed) otherCells.push({ rr, cc });
          }
        }
        const wordsNowVisible = (game.words || []).filter((w) =>
          isWordFullyVisible(w, placements, newRev)
        );
        const cellsInRevealedWords = new Set();
        for (const w of wordsNowVisible) {
          const cells = getWordCells(w, placements);
          for (const { r: cr, c: cc } of cells) {
            cellsInRevealedWords.add(`${cr},${cc}`);
          }
        }
        const partOfRevealedWord = otherCells
          .filter(({ rr, cc }) => cellsInRevealedWords.has(`${rr},${cc}`))
          .sort((a, b) => (a.rr !== b.rr ? a.rr - b.rr : a.cc - b.cc));
        const partOfRevealedSet = new Set(partOfRevealedWord.map(({ rr, cc }) => `${rr},${cc}`));
        const rest = otherCells
          .filter(({ rr, cc }) => !partOfRevealedSet.has(`${rr},${cc}`))
          .sort((a, b) => (a.rr !== b.rr ? a.rr - b.rr : a.cc - b.cc));
        for (const { rr, cc } of [...partOfRevealedWord, ...rest]) {
          delays[rr][cc] = idx * BASE_LOCAL_DELAY_MS;
          idx += 1;
        }
        setLocalRevealDelays(idx > 0 ? delays : null);

        // Houd het hele woord blauw tot de laatste flip klaar is, dan alles in één keer groen.
        const FLIP_DURATION_MS = 1100;
        const totalMs = idx > 0 ? (idx - 1) * BASE_LOCAL_DELAY_MS + FLIP_DURATION_MS : 0;
        const timers = pendingSolvedWordTimersRef.current;
        if (timers[guessUp]) window.clearTimeout(timers[guessUp]);
        setPendingSolvedWords((prev) => (prev.includes(guessUp) ? prev : [...prev, guessUp]));
        timers[guessUp] = window.setTimeout(() => {
          setPendingSolvedWords((prev) => prev.filter((w) => w !== guessUp));
          setLocalRevealDelays(null);
          timers[guessUp] = null;
        }, totalMs);

        // Tijdens deze woord-flip geen nieuwe acties toelaten
        startLocalAnimation(totalMs);
      }

    }

    const wordsVisibleAfter = game.words.filter((word) =>
      isWordFullyVisible(word, game.placements, newRev)
    );
    const newWordsRevealed = wordsVisibleAfter.filter((word) => !wordsVisibleBefore.includes(word));
    // Same reward as daily: 1 point for one completed word, 2 when two or more complete.
    // The correct on-board guess already counts as 1, so only extra words are stored as word_solved.
    const reward = Math.max(0, rewardForGuess(newWordsRevealed.length, isOnGrid));
    const extraWordSolvedCount = Math.max(0, reward - (isOnGrid ? 1 : 0));
    const wordsToScore = (isOnGrid
      ? newWordsRevealed.filter((word) => word !== guessUp)
      : newWordsRevealed
    ).slice(0, extraWordSolvedCount);
    const pointsAwarded = (isOnGrid ? 1 : 0) + wordsToScore.length;

    const wordSolvedActions = wordsToScore.map((word) => ({
      user: currentUserId,
      idNumber: game.idNumber,
      action: { type: "word_solved", word },
    }));
    const finalActions = [...updatedWithGuess, ...wordSolvedActions];
    playSequenceObjectRef.current = {
      ...playSequenceObjectRef.current,
      [String(game.idNumber)]: { idNumber: game.idNumber, actions: finalActions },
    };
    playSequenceActionsRef.current = finalActions;
    setPlaySequenceActions(finalActions);

    setGuess("");
    setRevealed(newRev);
    syncGuessedWithVisibility(newRev);
    void evaluateEndConditions(
      newRev,
      true,
      finalActions,
      pointsAwarded
    );

    // Bij game-einde doet evaluateEndConditions één save (acties + winner); geen tweede save plannen
    if (!allWordsSolved(newRev)) {
      const shouldSendPush = wasOurTurnBefore && derived.actionsThisTurn === 1;
      if (shouldSendPush) turnPlayerIdRef.current = opponentId;
      scheduleDebouncedBattleSave(shouldSendPush);
    }
  }

  // Who first revealed each cell (for lighter styling when revealed by opponent) - must run before any early return (hooks order)
  const revealedBy = useMemo(() => {
    if (!game?.board || !game?.placements || !currentUserId) return null;
    const emptyRevealed = () =>
      Array.from({ length: GRID_SIZE }, () => Array.from({ length: GRID_SIZE }, () => false));
    let rev = emptyRevealed();
    const by = Array.from({ length: GRID_SIZE }, () => Array.from({ length: GRID_SIZE }, () => null));
    const guessed = [];
    const actions = playSequenceActions || [];
    for (const action of actions) {
      const playerId = getActionPlayerId(action);
      const payload = getActionPayload(action);
      if (!payload || !payload.type) continue;
      const before = rev.map((row) => row.slice());
      if (payload.type === "click") {
        const { r, c } = payload;
        if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
          const letter = game.board[r][c];
          if (letter && letter !== "") {
            rev = revealAllOfLetter(letter, rev, game.board);
          } else {
            rev[r] = rev[r].slice();
            rev[r][c] = true;
          }
        }
      } else if (payload.type === "guess" && payload.revealsLetters) {
        const { word } = payload;
        if (word) {
          for (let i = 0; i < word.length; i++) {
            rev = revealAllOfLetter(word[i], rev, game.board);
          }
        }
      } else if (payload.type === "guess" && payload.correct) {
        const { word } = payload;
        if (word && !guessed.includes(word)) {
          guessed.push(word);
          rev = revealWord(word, game.placements, rev);
          for (let i = 0; i < word.length; i++) {
            rev = revealAllOfLetter(word[i], rev, game.board);
          }
        }
      } else if (payload.type === "word_solved" && payload.word) {
        const { word } = payload;
        rev = revealWord(word, game.placements, rev);
        for (let i = 0; i < word.length; i++) {
          rev = revealAllOfLetter(word[i], rev, game.board);
        }
      }
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          if (rev[r][c] && !before[r][c] && by[r][c] === null) {
            by[r][c] = playerId;
          }
        }
      }
    }
    return by;
  }, [playSequenceActions, game?.board, game?.placements, currentUserId]);

  // Range (start/end index) of the last opponent turn: alle acties van die beurt (max 2 turn-consumerende: click/guess)
  const lastOpponentTurnRange = useMemo(() => {
    if (!opponentId || !Array.isArray(playSequenceActions) || playSequenceActions.length === 0) {
      return null;
    }
    const actions = playSequenceActions;
    const lastIndex = actions.length - 1;
    const lastPlayerId = getActionPlayerId(actions[lastIndex]);
    if (!lastPlayerId || lastPlayerId !== opponentId) {
      return null;
    }
    let start = lastIndex;
    let turnConsumingCount = 0;
    for (let i = lastIndex; i >= 0; i--) {
      const pid = getActionPlayerId(actions[i]);
      if (!pid || pid !== opponentId) {
        start = i + 1;
        break;
      }
      if (isTurnConsumingAction(actions[i])) {
        turnConsumingCount++;
        if (turnConsumingCount > MAX_ACTIONS_PER_TURN) {
          start = i + 1;
          break;
        }
      }
      start = i;
    }
    return { start, end: lastIndex };
  }, [playSequenceActions, opponentId, MAX_ACTIONS_PER_TURN]);

  // Board state vóór de laatste tegenstander-beurt (voor replay één-voor-één)
  const revBeforeLastTurn = useMemo(() => {
    if (!game?.board || !game?.placements || !lastOpponentTurnRange) return null;
    const actions = playSequenceActions || [];
    const { start } = lastOpponentTurnRange;
    if (start <= 0) {
      const emptyRev = () =>
        Array.from({ length: GRID_SIZE }, () => Array.from({ length: GRID_SIZE }, () => false));
      return { rev: emptyRev(), guessed: [] };
    }
    const emptyRev = () =>
      Array.from({ length: GRID_SIZE }, () => Array.from({ length: GRID_SIZE }, () => false));
    return applyActionsRangeToRevealed(
      emptyRev(),
      [],
      actions,
      0,
      start - 1,
      game.board,
      game.placements
    );
  }, [playSequenceActions, lastOpponentTurnRange, game?.board, game?.placements]);

  // Replay-stappen: bij guess/word_solved één stap per nieuwe letter (woord volgorde)
  const expandedReplaySteps = useMemo(() => {
    if (!lastOpponentTurnRange || !revBeforeLastTurn || !game?.board || !game?.placements) return null;
    const actions = playSequenceActions || [];
    const { start, end } = lastOpponentTurnRange;
    return buildExpandedReplaySteps(
      actions,
      start,
      end,
      revBeforeLastTurn.rev,
      revBeforeLastTurn.guessed,
      game.board,
      game.placements
    );
  }, [lastOpponentTurnRange, revBeforeLastTurn, playSequenceActions, game?.board, game?.placements]);

  // Per woord: in welke replay-stap werd de laatste letter van dat woord getoond? (pas daarna groen)
  const lastReplayStepIndexByWord = useMemo(() => {
    if (!expandedReplaySteps || !game?.placements) return {};
    const actions = playSequenceActions || [];
    const map = {};
    for (let k = 0; k < expandedReplaySteps.length; k++) {
      const cells = getCellsRevealedByStep(expandedReplaySteps[k], actions, game?.board);
      for (const { r, c } of cells) {
        const words = getWordsForCell(r, c, game.placements);
        for (const w of words) {
          map[w] = k;
        }
      }
    }
    return map;
  }, [expandedReplaySteps, playSequenceActions, game?.board, game?.placements]);

  // Wat we tonen: tijdens replay alleen de state na 0..opponentReplayStep expanded stappen
  const displayedRevealed = useMemo(() => {
    if (!revealed || !game?.board) return revealed;
    if (!expandedReplaySteps || expandedReplaySteps.length === 0) return revealed;
    if (!revBeforeLastTurn) return revealed;
    if (opponentReplayStep >= expandedReplaySteps.length) return revealed;
    if (opponentReplayStep === 0) return revBeforeLastTurn.rev;
    let rev = revBeforeLastTurn.rev.map((row) => row.slice());
    let guessed = [...revBeforeLastTurn.guessed];
    const actions = playSequenceActions || [];
    for (let k = 0; k < opponentReplayStep; k++) {
      const next = applyExpandedStep(rev, guessed, expandedReplaySteps[k], actions, game.board, game.placements);
      rev = next.rev;
      guessed = next.guessed;
    }
    return rev;
  }, [revealed, revBeforeLastTurn, expandedReplaySteps, opponentReplayStep, playSequenceActions, game?.board, game?.placements]);

  // Cellen die déze replay-stap net zichtbaar zijn → flip-animatie. Alleen uit step-data (nooit oude letters).
  const replayFlipCells = useMemo(() => {
    if (!expandedReplaySteps || expandedReplaySteps.length === 0 || opponentReplayStep < 1) return null;
    const step = expandedReplaySteps[opponentReplayStep - 1];
    const cells = getCellsRevealedByStep(step, playSequenceActions || [], game?.board);
    if (cells.length === 0) return null;
    return cells.map((cell, idx) => ({ ...cell, delayIndex: idx }));
  }, [expandedReplaySteps, opponentReplayStep, playSequenceActions, game?.board]);

  // Reset replay bij nieuw spel
  useEffect(() => {
    setOpponentReplayStep(0);
  }, [game?.idNumber]);

  // Timer: elke 950ms één actie van de laatste tegenstander-beurt “replayen”
  useEffect(() => {
    if (!game || !expandedReplaySteps || expandedReplaySteps.length === 0 || isLoading) return;
    const numSteps = expandedReplaySteps.length;
    if (opponentReplayStep >= numSteps) return;
    let delayMs = opponentReplayStep === 0 ? 0 : 950;
    if (opponentReplayStep >= 1 && replayFlipCells && replayFlipCells.length > 0) {
      const maxDelayIndex = Math.max(...replayFlipCells.map((c) => c.delayIndex), 0);
      const FLIP_DURATION_MS = 1100;
      const minStepMs = (maxDelayIndex + 1) * FLIP_DURATION_MS;
      delayMs = Math.max(delayMs, minStepMs);
    }
    const t = setTimeout(() => setOpponentReplayStep((prev) => prev + 1), delayMs);
    return () => clearTimeout(t);
  }, [game, expandedReplaySteps, isLoading, opponentReplayStep, replayRestartKey, replayFlipCells]);

  // Eigen klik: pas helder (activated) zodra de flip-animatie van een cel begint (delay verstreken)
  useEffect(() => {
    flipStartedTimeoutsRef.current.forEach(clearTimeout);
    flipStartedTimeoutsRef.current = [];
    if (!localRevealDelays) {
      setCellsWithFlipStarted(new Set());
      return;
    }
    const initialStarted = new Set();
    const timeouts = [];
    for (let rr = 0; rr < GRID_SIZE; rr++) {
      for (let cc = 0; cc < GRID_SIZE; cc++) {
        const delay = localRevealDelays[rr]?.[cc];
        if (delay == null) continue;
        const key = `${rr},${cc}`;
        if (delay === 0) {
          initialStarted.add(key);
          continue;
        }
        const id = setTimeout(() => {
          setCellsWithFlipStarted((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
          });
        }, delay);
        timeouts.push(id);
      }
    }
    setCellsWithFlipStarted(initialStarted);
    flipStartedTimeoutsRef.current = timeouts;
    return () => {
      timeouts.forEach(clearTimeout);
      flipStartedTimeoutsRef.current = [];
    };
  }, [localRevealDelays]);

  // Auto replay: pas helder (activated) zodra de flip-animatie van een cel begint (delay verstreken)
  useEffect(() => {
    replayFlipStartedTimeoutsRef.current.forEach(clearTimeout);
    replayFlipStartedTimeoutsRef.current = [];
    if (!replayFlipCells || replayFlipCells.length === 0) {
      setReplayCellsWithFlipStarted(new Set());
      return;
    }
    const REPLAY_DELAY_MS = 1100;
    const initialStarted = new Set();
    const timeouts = [];
    for (const { r, c, delayIndex } of replayFlipCells) {
      const key = `${r},${c}`;
      const delay = delayIndex * REPLAY_DELAY_MS;
      if (delay === 0) {
        initialStarted.add(key);
        continue;
      }
      const id = setTimeout(() => {
        setReplayCellsWithFlipStarted((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      }, delay);
      timeouts.push(id);
    }
    setReplayCellsWithFlipStarted(initialStarted);
    replayFlipStartedTimeoutsRef.current = timeouts;
    return () => {
      timeouts.forEach(clearTimeout);
      replayFlipStartedTimeoutsRef.current = [];
    };
  }, [replayFlipCells]);

  // Automatisch naar volgende game:
  // - alleen als er nog een volgende game is in battleOrder
  // - na einde van alle animaties (replay of lokale animaties) + 1 seconde extra
  useEffect(() => {
    if (!game || battleIsOverForUI) return;
    if (!hasNextGameInBattleOrder) return;
    if (!(hasWon || hasLost)) return;

    const hasReplay = expandedReplaySteps && expandedReplaySteps.length > 0;
    const replayDone = !hasReplay || opponentReplayStep >= (expandedReplaySteps?.length || 0);
    if (!replayDone) return;
    if (isLocalAnimating) return;

    hasAutoAdvancedRef.current = false;

    const timeout = setTimeout(() => {
      handleNextWordHit();
    }, GRID_FLIP_DURATION_MS + 1000);

    return () => clearTimeout(timeout);
    const FALLBACK_EXTRA_MS = 3000;
    if (autoAdvanceFallbackTimeoutRef.current) {
      clearTimeout(autoAdvanceFallbackTimeoutRef.current);
      autoAdvanceFallbackTimeoutRef.current = null;
    }
    autoAdvanceFallbackTimeoutRef.current = setTimeout(() => {
      // Als de auto-next om wat voor reden dan ook niet is gegaan,
      // maar we zitten nog steeds in dezelfde situatie, doe het alsnog.
      if (hasAutoAdvancedRef.current) return;
      if (!game || battleIsOverForUI) return;
      if (!hasNextGameInBattleOrder) return;
      if (!(hasWon || hasLost)) return;

      const stillHasReplay = expandedReplaySteps && expandedReplaySteps.length > 0;
      const stillReplayDone = !stillHasReplay || opponentReplayStep >= (expandedReplaySteps?.length || 0);
      if (!stillReplayDone) return;
      if (isLocalAnimating) return;

      handleNextWordHit();
    }, GRID_FLIP_DURATION_MS + 1000 + FALLBACK_EXTRA_MS);

    return () => {
      clearTimeout(timeout);
      if (autoAdvanceFallbackTimeoutRef.current) {
        clearTimeout(autoAdvanceFallbackTimeoutRef.current);
        autoAdvanceFallbackTimeoutRef.current = null;
      }
    };
  }, [game?.idNumber, hasWon, hasLost, battleIsOver, isLocalAnimating, hasNextGameInBattleOrder, expandedReplaySteps, opponentReplayStep]);

  // Initial load (no game yet): show fullscreen loading so underlying pages are not visible
  if (isLoading && !game) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "var(--sf-bg, #fefbef)",
          zIndex: 9999,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="wordhit-battle-loading-spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="wordhit-wrapper">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ color: "var(--sf-red, #e62731)" }}>{loadError}</p>
          <button
            onClick={onClose}
            style={{
              marginTop: "20px",
              padding: "12px 24px",
              backgroundColor: "var(--sf-blue, #194093)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            {langString === "nl" ? "Sluiten" : "Close"}
          </button>
        </div>
      </div>
    );
  }

  if (!game) {
    console.log("[WordHitBattle] No game loaded");
    return (
      <div className="wordhit-wrapper">
        <div style={{ padding: "40px", textAlign: "center" }}>
          {langString === "nl" ? "Geen game geladen" : "No game loaded"}
        </div>
      </div>
    );
  }

  if (!game.board || !Array.isArray(game.board) || game.board.length === 0) {
    console.error("[WordHitBattle] Invalid board:", game.board);
    return (
      <div className="wordhit-wrapper">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ color: "var(--sf-red, #e62731)" }}>
            {langString === "nl" ? "Ongeldig board" : "Invalid board"}
          </p>
        </div>
      </div>
    );
  }

  const rowCounts = game.board.map(
    (row) => row.filter((cell) => cell !== "").length
  );

  const colCounts = Array.from({ length: GRID_SIZE }, (_, c) => {
    let count = 0;
    for (let r = 0; r < GRID_SIZE; r++) {
      if (game.board[r][c] !== "") count++;
    }
    return count;
  });

  function getCellClass(r, c, opts = {}) {
    const { normalStyle = false } = opts; // true = niet tot opponent's last turn, gewoon zoals normale WordHit (geen cell--by-opponent)
    const vis = displayedRevealed[r][c];
    const letter = game.board[r][c];

    if (!vis) {
      return "cell cell-hidden";
    }

    const isBoardFullyRevealed = displayedRevealed.every((row) =>
      row.every((cell) => cell === true)
    );

    const byOpponent = !normalStyle && revealedBy && opponentId && revealedBy[r]?.[c] === opponentId;
    const suffixByOpponent = byOpponent ? " cell--by-opponent" : "";

    if (!letter) {
      if (isBoardFullyRevealed) return "cell cell-hidden";
      return "cell cell-miss" + suffixByOpponent;
    }

    // Tijdens replay: woord pas groen nadat de laatste letter van dat woord geflipt is (stap voorbij)
    const inReplay = expandedReplaySteps && opponentReplayStep < expandedReplaySteps.length;
    let solved = isCellInFullyVisibleWord(r, c, game.placements, displayedRevealed);
    // Tijdens eigen word guess: woord pas groen nadat de laatste letter geflipt is.
    // Maar: als deze cel al groen was door een ander (niet-pending) woord,
    // dan mag hij nooit meer terug naar blauw.
    if (solved && !byOpponent && pendingSolvedWords && pendingSolvedWords.length > 0) {
      const wordsHere = getWordsForCell(r, c, game.placements);
      const solvedWordsHere = wordsHere.filter((w) =>
        isWordFullyVisible(w, game.placements, displayedRevealed)
      );
      const hasSolvedNonPendingWord = solvedWordsHere.some(
        (w) => !pendingSolvedWords.includes(w)
      );
      if (!hasSolvedNonPendingWord) {
        solved = false;
      }
    }
    if (solved && inReplay && lastReplayStepIndexByWord) {
      const wordsHere = getWordsForCell(r, c, game.placements);
      for (const w of wordsHere) {
        const lastStep = lastReplayStepIndexByWord[w];
        if (lastStep !== undefined && opponentReplayStep <= lastStep) {
          solved = false;
          break;
        }
      }
    }

    if (solved) {
      return "cell cell-solved" + suffixByOpponent;
    }
    return "cell cell-revealed" + suffixByOpponent;
  }

  function handleBattleWrapperClick(e) {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest(".guess-form")) return;
    if (target.closest(".cell")) return; // klik op letter/miss-cel: eigen handler, geen replay-restart
    // Buiten grid en buiten guess: auto-replay opnieuw starten + flash
    if (expandedReplaySteps && expandedReplaySteps.length > 0) {
      setReplayRestartKey((k) => k + 1);
      setOpponentReplayStep(0);
    }
    setFlashRevealed(false);
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => setFlashRevealed(true));
    } else {
      setTimeout(() => setFlashRevealed(true), 0);
    }
  }

  // Check if Battle Mode is enabled (after all hooks to satisfy Rules of Hooks)
  if (!isBattleModeEnabled()) {
    return null;
  }

  const nextWordHitDisabled =
    isSavingGameResult ||
    isLoading ||
    isOffline ||
    !hasNextGameInBattleOrder ||
    battleIsOverForUI;

  return (
    <>
      {showConfetti && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10005, pointerEvents: "none" }}>
          <ConfettiOverlay visible={true} colors={["var(--sf-green, #28ab64)", "var(--sf-blue, #194093)", "#f5d547", "#e62731"]} />
        </div>
      )}
      {showChatModal && (
        <BattleChatModal
          onClose={() => setShowChatModal(false)}
          chatMessages={chatMessages}
          currentUserId={currentUserId}
          opponentName={opponent?.get?.("name") || opponent?.name || ""}
          langString={effectiveLangString}
          onSendMessage={handleSendChatMessage}
          onMarkRead={handleMarkChatRead}
          isLoading={chatSending}
        />
      )}
      {/* Wrong guessed words modal - same as normal WordHit */}
      {showWrongWordsModal && (
        <>
          <div
            className="rules-overlay"
            onClick={() => setShowWrongWordsModal(false)}
            style={{ zIndex: 10000 }}
          />
          <div
            className="wrong-words-modal"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10001,
              background: "var(--sf-bg)",
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              padding: "20px 25px",
              maxWidth: "320px",
              width: "85%",
              textAlign: "center",
            }}
          >
            <button
              className="wrong-words-close"
              onClick={() => setShowWrongWordsModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                color: "var(--sf-blue)",
                lineHeight: 1,
                padding: "4px",
              }}
              aria-label={langString === "nl" ? "Sluiten" : "Close"}
            >
              ✕
            </button>
            <h2 style={{
              fontFamily: "Union, sans-serif",
              fontSize: "1.6rem",
              fontWeight: "normal",
              color: "var(--sf-blue)",
              margin: "0 0 20px 0",
            }}>
              {langString === "nl" ? "Fout geraden woorden" : "Wrong guessed words"}
            </h2>
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              alignItems: "center",
              maxHeight: "300px",
              overflowY: "auto",
              overflowX: "hidden",
            }}>
              {wrongGuessedWords.length === 0 ? (
                <p style={{
                  fontFamily: "Union, sans-serif",
                  fontSize: "1.3rem",
                  color: "#666",
                  margin: 0,
                }}>
                  {langString === "nl" ? "Geen fout geraden woorden" : "No wrong guessed words"}
                </p>
              ) : (
                wrongGuessedWords.map((word, index) => (
                  <div
                    key={index}
                    style={{
                      fontFamily: "Union, sans-serif",
                      fontSize: "1.4rem",
                      fontWeight: "normal",
                      color: "var(--sf-red)",
                      padding: "8px 16px",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    {word}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
      {/* Fullscreen overlay - covers entire screen */}
      <div 
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "var(--sf-bg, #fefbef)",
          zIndex: 9999,
          overflow: "auto",
          width: "100vw",
          height: "100vh",
        }}
        className="wordhit-battle-fullscreen"
        onClick={(e) => {
          // Don't close if clicking inside the content
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        onTouchStart={handleBattleTouchStart}
        onTouchEnd={handleBattleTouchEnd}
      >
        {/* Offline warning banner - when no internet, show at top and game is deactivated */}
        {isOffline && (
          <div className="wordhit-battle-offline-banner" role="alert">
            <WifiOff size={22} strokeWidth={2} aria-hidden />
            <span>
              {effectiveLangString === "nl"
                ? "Geen internetverbinding. Het spel is gepauzeerd."
                : "No internet connection. Game paused."}
            </span>
          </div>
        )}
        <div
          className={
            "wordhit-wrapper wordhit-battle-wrapper" +
            (flashRevealed ? " wordhit-battle-wrapper--flash-revealed" : "") +
            (derivedTurnForUI?.turnPlayerId === opponentId
              ? " wordhit-battle-wrapper--opponent-turn"
              : " wordhit-battle-wrapper--user-turn")
          }
          style={{ position: "relative", width: "100%", minHeight: "100vh" }}
          onClick={handleBattleWrapperClick}
        >
          <button
            onClick={onClose}
            className="wordhit-battle-close-button"
            aria-label={langString === "nl" ? "Sluiten" : "Close"}
          >
            ✕
          </button>

      <div className="top-header">
        <div className="title-row">
          <div className="logo-column">
            <div className="widget-header wordhit-battle-header">
              <div className="green-line-thin"></div>
              <div className="green-line-thick"></div>
              <div className="yellow-block"></div>
              {/* Hide squares in Battle Mode */}
              <div className="green-outline-left"></div>
              <img src={`${process.env.PUBLIC_URL || ""}/battle-logo.png`} alt="Battle Mode" className="wordhit-battle-logo" />
              {/* Players names: directly under Battle Mode */}
              {(currentUser || opponent) && (
                <div style={{
                  textAlign: "center",
                  marginTop: "6px",
                  fontSize: "1.4rem",
                  color: "var(--text-dark, #1a1a1a)",
                  fontWeight: "normal",
                }}>
                  {currentUser?.get?.("name") || "Jij"}
                  <span style={{ fontSize: "1.5rem", fontWeight: "600", marginLeft: "6px", marginRight: "4px", color: "var(--sf-green, #28ab64)" }}>
                    {battleTotals?.[currentUserId] ?? 0}
                  </span>
                  <span style={{ fontSize: "1.5rem", fontWeight: "600", color: "var(--sf-green, #28ab64)" }}> – </span>
                  <span style={{ fontSize: "1.5rem", fontWeight: "600", marginRight: "6px", marginLeft: "4px", color: "var(--sf-green, #28ab64)" }}>
                    {battleTotals?.[opponentId] ?? 0}
                  </span>
                  {opponent?.name || (langString === "nl" ? "Tegenstander" : "Opponent")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Turn info: direct onder header, buiten de header */}
        {!battleIsOverForUI && (
          <div style={{
            textAlign: "center",
            marginTop: "4px",
            marginBottom: "0",
            fontSize: "1.4rem",
            color: derivedTurnForUI?.turnPlayerId === currentUserId ? "var(--sf-green, #28ab64)" : "var(--sf-blue, #194093)",
            minHeight: "1.4rem",
          }}>
            {(!hasWon && !hasLost) && (
              <>
                {derivedTurnForUI?.turnPlayerId == null
                  ? (langString === "nl" ? "Wachten op een tegenstander" : "Waiting for an opponent")
                  : derivedTurnForUI.turnPlayerId === currentUserId
                  ? (langString === "nl"
                    ? `Jij bent aan de beurt - ${derivedTurnForUI.actionsThisTurn ?? 0} / ${MAX_ACTIONS_PER_TURN}`
                    : `You are on turn - ${derivedTurnForUI.actionsThisTurn ?? 0} / ${MAX_ACTIONS_PER_TURN}`)
                  : (langString === "nl"
                    ? `${opponent?.name || "Tegenstander"} is aan de beurt`
                    : `${opponent?.name || "Opponent"} is on turn`)}
              </>
            )}
          </div>
        )}

        {/* Battle over: first to BATTLE_WIN_WORD_COUNT words wins */}
        {battleIsOverForUI && (
          <div style={{
            textAlign: "center",
            marginTop: "12px",
            padding: "12px 16px",
            backgroundColor: "var(--sf-green, #28ab64)",
            borderRadius: "0",
            fontSize: "1.4rem",
            color: "var(--sf-bg, #fefbef)",
          }}>
            {battleWinnerIdForUI && battleWinnerIdForUI === currentUserId ? (
              <>
                {langString === "nl" ? "Gefeliciteerd!" : "Congratulations!"}
                <br />
                {langString === "nl" ? "Jij wint de battle!" : "You win the battle!"}
              </>
            ) : (
              <>
                {langString === "nl" ? `${opponent?.name || "Tegenstander"} wint de battle` : `${opponent?.name || "Opponent"} wins the battle`}
                <br />
                {langString === "nl" ? "Volgende keer beter" : "Better luck next time"}
              </>
            )}
          </div>
        )}
      </div>

      <div className="wordhit-battle-grid-align">
      {/* Thema los, zelfde volgorde en afstand als normale WH */}
      <div className={`theme-inline wordhit-battle-theme-inline${isNextTransitioning ? " wordhit-battle-theme-inline--next-flip" : ""}`}>
        <span className="theme-label">
          {effectiveLangString === "nl" ? "Thema: " : "Theme: "}
        </span>
        <span className="theme-name">{game.theme}</span>
      </div>

        <div className={`board-outer${isNextTransitioning ? " wordhit-battle-board-outer--next-flip" : ""}`}>
          {/* Chatknop rechtsboven grid,zelfde positie als kliks-teller op normale WH */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowChatModal(true); }}
            className="wordhit-battle-chat-button"
            aria-label={effectiveLangString === "nl" ? "Chat" : "Chat"}
            title={effectiveLangString === "nl" ? "Chat" : "Chat"}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {hasChatUnread && (
              <span
                className="wordhit-battle-chat-unread"
                aria-hidden="true"
              />
            )}
          </button>
          {game.board.map((row, r) => (
            <div className="board-row" key={`row-${r}`}>
              {row.map((cell, c) => {
                const canClick =
                  !isOffline &&
                  !hasWon &&
                  !hasLost &&
                  !battleIsOver &&
                  derivedTurnForUI?.turnPlayerId === currentUserId &&
                  (derivedTurnForUI?.actionsThisTurn ?? 0) < MAX_ACTIONS_PER_TURN;

                // Niet tot opponent's last turn = al zichtbaar vóór die beurt
                const notOpponentLastTurn = !!revBeforeLastTurn?.rev?.[r]?.[c];

                // Voor eigen animaties (klik/guess) gebruiken we localRevealDelays direct;
                // replay gebruikt exclusief cell--replay-flip.
                const hasLocalDelay =
                  localRevealDelays &&
                  localRevealDelays[r] &&
                  localRevealDelays[r][c] != null;

                const inReplayFlipList = replayFlipCells?.some((p) => p.r === r && p.c === c);
                // Alleen flippen als cel in step-lijst staat én niet al vóór laatste beurt zichtbaar was
                const wasAlreadyVisible = !!revBeforeLastTurn?.rev?.[r]?.[c];
                const hasReplayFlip = inReplayFlipList && !wasAlreadyVisible;
                const replayDelayIndex =
                  hasReplayFlip
                    ? replayFlipCells?.find((p) => p.r === r && p.c === c)?.delayIndex ?? 0
                    : null;

                const extraLocalClass =
                  notOpponentLastTurn && !hasLocalDelay && !hasReplayFlip
                    ? " cell--no-flip"
                    : (hasLocalDelay ? " cell--local-flip" : "") +
                      (hasReplayFlip ? " cell--replay-flip" : "");

                // Zelfde zichtbaarheid als normale WH voor eigen animaties:
                // - als we een lokale delay hebben (eigen klik/guess), gebruik basis revealed
                // - anders gebruik displayedRevealed (voor replay / opponent)
                const isUserLocalAnimCell = hasLocalDelay && !hasReplayFlip;
                const isVisible = isUserLocalAnimCell ? revealed[r][c] : displayedRevealed[r][c];

                return (
                  <div
                    key={`${r}-${c}`}
                    onClick={() => clickCell(r, c)}
                    className={getCellClass(r, c, { normalStyle: notOpponentLastTurn }) + extraLocalClass}
                    style={{
                      cursor: canClick && !isVisible ? "pointer" : "not-allowed",
                      opacity: (() => {
                        if (!canClick && !isVisible) return 0.5;
                        if (hasLocalDelay && !cellsWithFlipStarted.has(`${r},${c}`)) return 0.5;
                        if (hasReplayFlip && !replayCellsWithFlipStarted.has(`${r},${c}`)) return 0.5;
                        return 1;
                      })(),
                      ...(hasLocalDelay
                        ? {
                            "--local-reveal-delay": `${localRevealDelays[r][c]}ms`,
                            animationDelay: `${localRevealDelays[r][c]}ms`,
                          }
                        : replayDelayIndex != null
                          ? {
                              "--local-reveal-delay": `${replayDelayIndex * 1100}ms`,
                              animationDelay: `${replayDelayIndex * 1100}ms`,
                            }
                          : {}),
                    }}
                  >
                    {isVisible ? <span className="cell-letter">{cell}</span> : ""}
                  </div>
                );
              })}
              <div className="count-cell">{rowCounts[r]}</div>
            </div>
          ))}

          <div className="board-row board-row-counts">
            {colCounts.map((count, i) => (
              <div className="count-cell" key={`col-${i}`}>
                {count}
              </div>
            ))}
            <div className="count-cell corner-cell"></div>
          </div>

          {showCheckOverlay && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.6)",
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width={180}
                  height={180}
                  viewBox="0 0 120 120"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle
                    cx="60"
                    cy="60"
                    r="52"
                    fill="var(--sf-green, #4caf50)"
                  />
                  <polyline
                    points="35,65 52,80 85,40"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          )}

          {showCrossOverlay && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.6)",
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  fontFamily: "Union, sans-serif",
                  fontSize: 150,
                  fontWeight: "bold",
                  color: "var(--sf-red, #e62731)",
                }}
              >
                ❌
              </div>
            </div>
          )}
        </div>

        </div>
        {/* end wordhit-battle-grid-align */}

        {!battleIsOverForUI && (
          !(hasWon || hasLost) ? (
            <form onSubmit={submitGuess} className="guess-form">
              {guessFeedback && (
                <p className="guess-feedback guess-feedback--bad">{guessFeedback}</p>
              )}
              <div className={`guess-form-row${guessShake ? " guess-form-row--shake" : ""}`}>
                {wrongGuessedWords.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowWrongWordsModal(true)}
                    className="wrong-words-button"
                    aria-label={langString === "nl" ? "Fout geraden woorden" : "Wrong guessed words"}
                  >
                    <ListX size={20} strokeWidth={2.5} />
                  </button>
                )}
                <input
                  type="text"
                  value={guess}
                  onChange={handleGuessChange}
                  placeholder={
                    langString === "nl"
                      ? "Raad een woord..."
                      : "Guess a word..."
                  }
                  className="guess-input"
                  disabled={hasWon || hasLost || battleIsOverForUI || isOffline || derivedTurnForUI?.turnPlayerId !== currentUserId || (derivedTurnForUI?.actionsThisTurn ?? 0) >= MAX_ACTIONS_PER_TURN}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <button
                  type="submit"
                  disabled={!guess.trim() || hasWon || hasLost || battleIsOverForUI || isOffline || derivedTurnForUI?.turnPlayerId !== currentUserId || (derivedTurnForUI?.actionsThisTurn ?? 0) >= MAX_ACTIONS_PER_TURN}
                  className="guess-button"
                >
                  <Check size={20} strokeWidth={2.5} />
                </button>
              </div>
            </form>
          ) : (
            // Placeholder om dezelfde verticale ruimte te behouden zodra de game klaar is
            <div className="guess-form guess-form--placeholder" aria-hidden="true" />
          )
        )}

        {/* Game Finished Popup - Removed: button now pulses instead */}
        </div>

        {battleIsOverForUI &&
          battleWinnerIdForUI === currentUserId &&
          opponentId &&
          typeof onNavigateToNewBattle === "function" && (
            <div className="wordhit-battle-rematch-bar">
              <button
                type="button"
                className="wordhit-battle-rematch-button"
                onClick={() => {
                  void handleRematchSameOpponent();
                }}
                disabled={isRematching || isOffline}
              >
                {isRematching
                  ? effectiveLangString === "nl"
                    ? "Bezig…"
                    : "Loading…"
                  : effectiveLangString === "nl"
                    ? `nieuwe battle tegen ${opponent?.name || battleInfo?.opponent?.name || "tegenstander"}`
                    : `New battle against ${opponent?.name || battleInfo?.opponent?.name || "opponent"}`}
              </button>
            </div>
          )}

        {/* Loading overlay: alleen bij eerste load (geen game) om onderliggende pagina te verbergen */}
        {isLoading && !game && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              zIndex: 50,
              pointerEvents: "auto",
            }}
            aria-label={langString === "nl" ? "Laden..." : "Loading..."}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="wordhit-battle-loading-spinner" aria-hidden="true" />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default WordHitBattle;
